"use client"
import { createContext, useContext, useEffect, useId, useMemo, useState, type ReactNode } from "react"
import { LayoutGroup, motion } from "motion/react"
import {
  useBandHudOverlay,
  type PointHoverTooltipPayload,
} from "../../../extrapolated-graph/BandHudOverlayChannel"
import {
  CURRENT_LEVEL_RISK_TED_LABEL,
  TAKEOVER_RISK_LEVEL_LADDER,
  TAKEOVER_RISK_LEVEL_LADDER_EXTENDED,
  TAKEOVER_RISK_LIKELIHOOD_DATA,
  TAKEOVER_RISK_YEARLY_POINTS,
  currentLevelRiskAt,
  fmtLikelihood,
  likelihoodAtYear,
  type RiskAreaLikelihoodPoint,
  type RiskLevelLadderEntry,
} from "../../../extrapolated-graph/takeoverRiskLikelihoodData"
import { useHudContext, useHudDates } from "../../shared/playbookHUDInfra"
import { capabilityLevelLabelAtYear } from "./useCurrentCapabilityStats"
import {
  HoverSwapFadeOverlay,
  hoverSwapHiddenAndUntargetableContentStyle,
  useHoverSwapSequentialFade,
} from "../hoverSwapFade"
import {
  HUD_HOVER_RULE_CENTERED_TOP_PX,
  HudHoverClickHint,
  HudHoverDescription,
  HudHoverDetailBody,
  HudHoverDetailRule,
  HudHoverRuleTreatmentContext,
  HudHoverTakeoverDetail,
  HudHoverTitle,
  TAKEOVER_DETAIL_TOP_NUDGE_PX,
} from "../hudHoverDetailHeader"
import { useHoverSwapsDisabled } from "../hoverSwapsDisabled"

// ─── Editable content ────────────────────────────────────────────────────────
// Copy for the in-canvas AI-capabilities takeover: hovering the capability
// line replaces the HUD's lower half with this explainer.
export const CAPABILITY_HOVER_COPY = {
  title: "AI Capabilities Line",
  subtitle: "How powerful we predict AI will be at this time.",
  speedupLabel: "R&D Speedup",
}

// Copy for the takeover-risk AREA hover: hovering the red dither field ABOVE
// the capability line (rather than the line itself) swaps the HUD's lower
// half to this explainer while the field lightens on the graph (see
// TAKEOVER_RISK_AREA_HOVER_OPACITY in ExtrapolatedGraphRenderer).
export const TAKEOVER_RISK_HOVER_COPY = {
  title: "AI Takeover Risk",
  description:
    "Our extremely rough guess on how likely AI is to disempower humanity if scaled rapidly to Top-Expert-Dominating level of capabilities, with that year's alignment techniques. The denser the dots, the higher the risk.",
}

// Tab 34 (2026-07-15): gap between the red dither swatch and the "AI
// Takeover Risk" title when the field hover's panel carries the field's icon
// next to its name (riskHoverTitleSwatch).
export const RISK_HOVER_TITLE_SWATCH_GAP_PX = 7

// Copy for the COMBINED hover (tab 14, riskCombinedDetail): the whole graph
// is one hover — the takeover swaps the lower half to this explainer, which
// carries BOTH the capability point (R&D speedup + ladder, like the line
// hover) and the foom takeover risk at the cursor's year (like the
// risk-field hover). DRAFT copy — edit freely.
export const COMBINED_CAPABILITY_RISK_HOVER_COPY = {
  title: "AI Capabilities & Takeover Risk",
  description:
    "The projected level of AI capability — and how likely AI is to disempower humanity if capabilities foomed this year, with that year's alignment techniques.",
  speedupLabel: "R&D Speedup",
  riskLabel: "Takeover Risk if foomed",
  riskValueColor: "var(--accent)",
}

// The combined hover's OTHER risk semantics (tab 19,
// combinedRiskDetail: "current-level"): not "if foomed to TED" but the risk
// of the capability level the line actually reaches at the hovered year, so
// the percentage climbs as you sweep into years with higher capabilities.
// DRAFT copy — edit freely.
export const COMBINED_CURRENT_LEVEL_RISK_COPY = {
  description:
    "The projected level of AI capability — and how likely AI at that level is to disempower humanity, given that year's projected alignment techniques.",
  riskLabel: "Takeover Risk at this level",
}

// The current-level risk number lives with the takeover table —
// see currentLevelRiskAt in takeoverRiskLikelihoodData.ts (Joanna's
// TED-authored value at the year, scaled by the FRACTIONAL ladder rung
// log-interpolated from the capability value, capped at 100%).

// Which risk the combined hover's stat carries — see the copy consts above.
export type CombinedRiskSemantics = "foom" | "current-level"

// The combined hover's TWO-SECTION layout (tabs 22–24): the capability
// hover's own header block on top (title, subtitle, R&D-speedup stat,
// ladder), then an "AI Takeover Risk" header section under it carrying the
// field hover's simple definition (TAKEOVER_RISK_HOVER_COPY.description)
// and, per combinedRiskLine, one live percentage line.
export type CombinedRiskLayout = "two-sections"
//   "risk-level"   — "Risk level: 5%" (the current-level risk at the
//                    hovered year, tab 19's semantics)
//   "ted-this-year" — "Risk if Top-Expert-Dominating AI level was achieved
//                    this year: 80%" (the table's value at the hovered year)
export type CombinedRiskLine = "risk-level" | "ted-this-year"

export const COMBINED_RISK_LINE_COPY = {
  riskLevelLabel: "Risk level:",
  tedThisYearLabel:
    "Risk if Top-Expert-Dominating AI level was achieved this year:",
}

export const COMBINED_RISK_LINE_STYLE = {
  fontSize: "11px",
  valueColor: "var(--accent)",
  marginTopPx: 6,
  labelValueGapPx: 5,
}

// Vertical gap between the capabilities block and the risk section.
export const COMBINED_TWO_SECTION_GAP_PX = 14

// The in-column agent-level list ("cells-def-columns-ladder-in-column"):
// names only with the gliding red mark, plus ONE reserved description slot
// showing the active level's one-liner — fixed height, so nothing shifts as
// the level travels.
export const COMPACT_LEVEL_LIST_STYLE = {
  // Stepped DOWN a size from the first take (11px rows, 3px bar) — Joanna
  // 2026-07-15: the list was more salient than the data cells above it.
  marginTopPx: 12,
  rowPaddingYPx: 1,
  fontSizePx: 9.5,
  barWidthPx: 2,
  barGapPx: 6,
  barColor: "var(--accent)",
  currentColor: "var(--accent)",
  descriptionFontSizePx: 9.5,
  descriptionMarginTopPx: 5,
  // Reserved height for the active one-liner (em of its own font) — two
  // lines, so long and short descriptions occupy the same space.
  descriptionSlotEm: 2.95,
}

const CompactLevelList = ({ currentLabel }: { currentLabel: string | null }) => {
  const style = COMPACT_LEVEL_LIST_STYLE
  const current = CAPABILITY_LEVEL_LADDER.find((level) => level.label === currentLabel)
  return (
    <div style={{ marginTop: style.marginTopPx }}>
      {CAPABILITY_LEVEL_LADDER.map((level) => {
        const isCurrent = level.label === currentLabel
        return (
          <div
            key={level.label}
            className="relative flex items-center font-mono whitespace-nowrap"
            style={{
              paddingTop: style.rowPaddingYPx,
              paddingBottom: style.rowPaddingYPx,
              paddingLeft: style.barWidthPx + style.barGapPx,
              fontSize: style.fontSizePx,
              fontWeight: isCurrent ? 700 : 400,
              color: isCurrent ? style.currentColor : undefined,
              transition: CAPABILITY_LADDER_ROW_TRANSITION_CSS,
            }}
          >
            {isCurrent && (
              <motion.span
                layoutId="compact-level-list-bar"
                className="absolute left-0 top-0 bottom-0"
                style={{ width: style.barWidthPx, background: style.barColor }}
                transition={CAPABILITY_LADDER_HIGHLIGHT_TRANSITION}
              />
            )}
            {level.label}
          </div>
        )
      })}
      {/* the single active definition, in a fixed-height slot */}
      <div
        className="font-sans"
        style={{
          marginTop: style.descriptionMarginTopPx,
          fontSize: style.descriptionFontSizePx,
          lineHeight: 1.45,
          minHeight: `${style.descriptionSlotEm}em`,
        }}
      >
        {current?.description ?? ""}
      </div>
    </div>
  )
}

// ─── Tab 76's CLARITY variants (ladderColumnClarityDesign) ───────────────────
// How the ladder-in-column panel names its two sides (Joanna's two seed
// ideas: side labels "AI Capabilities"/"Takeover Risk"; labels UNDER the
// numbers so the digits top the hierarchy):
//   "headers"         — a small caps header on each column ("AI
//                       CAPABILITIES" / "TAKEOVER RISK") above the digits
//   "labels-under"    — no headers; both digits carry their small label
//                       directly underneath (digits highest)
//   "headers-no-defs" — headers + digits + the R&D label, the definition
//                       prose REMOVED — clarity by subtraction
//   "header-icon"     — the icons move UP into the headers ([glyph] AI
//                       CAPABILITIES), the digits stand alone below
//   "value-first"     — labels-under compressed: short risk label, 9px
//                       definitions — maximum digit dominance
//   "big-headers"     — the TITLE's spot goes to the individual names: two
//                       title-tier headers, one per column ([glyph] AI
//                       CAPABILITIES / [swatch] TAKEOVER RISK), no shared
//                       panel title
//   "big-headers-single-level" — big-headers with the level LIST replaced
//                       by a single readout: the current agent level under
//                       the speedup digit, its one-liner beneath
//   "level-dots"      — 84 + 85 composed (Joanna 2026-07-15): single level
//                       readout in BLACK, the capability sentence dropped,
//                       and a tiny o-o-o-o-o-o-o strip of the seven levels
//                       (current solid, others outline) beneath
//   "level-plain-spaced" — 86 without the dots, with extra air between the
//                       speedup block and the level readout (pairs with
//                       capabilityHoverRowOnly so the labor section stays
//                       visible below)
//   The 87 clarity-fix round (2026-07-15, one fix per Claude's critique):
//   "year-pinned"     — a small "in 2027" at the panel's top-right pins
//                       every live value to the cursor's moment
//   "level-first"     — the LEVEL leads the capability column (the human
//                       fact), the 1.9x measure beneath it
//   "risk-percent"    — the word gets its ruler: "Medium-Low · 22%"
//   "risk-scale-dots" — the word's six-band scale as a tiny red dot strip
//                       beside it (86's dot grammar, applied to risk)
//   "clarity-all"     — all four fixes together
export type LadderColumnClarityDesign =
  | "headers"
  | "labels-under"
  | "headers-no-defs"
  | "header-icon"
  | "value-first"
  | "big-headers"
  | "big-headers-single-level"
  | "level-dots"
  | "level-plain-spaced"
  | "year-pinned"
  | "level-first"
  | "risk-percent"
  | "risk-scale-dots"
  | "clarity-all"

export const LADDER_COLUMN_CLARITY_COPY = {
  capabilityHeader: "AI Capabilities",
  riskHeader: "Takeover Risk",
  riskLabelShort: "Takeover Risk",
}

export const LADDER_COLUMN_CLARITY_STYLE = {
  headerFontSizePx: 8.5,
  headerMarginBottomPx: 8,
  headerIconGapPx: 5,
  tightDefFontSizePx: 9,
  // The digits (3.8x / Medium-Low) match the level name's 11px
  // (SINGLE_LEVEL_READOUT_STYLE.nameFontSizePx — Joanna 2026-07-15; the
  // shared cells family keeps its 13px).
  valueFontSizePx: 11,
  // Digit → definition gap matches the level name → one-liner gap
  // (SINGLE_LEVEL_READOUT_STYLE.descriptionMarginTopPx).
  defMarginTopPx: 3,
  // Title-tier headers get icons sized to match the title's presence
  // (Joanna 2026-07-15): the glyph scales up, the swatch uses the full
  // four-row radii instead of the footnote's three.
  titleHeaderGlyphSizePx: 18,
  // The riskHeaderRed designs' header ink (tab 89).
  redHeaderColor: "var(--accent)",
}

// The single agent-level readout ("big-headers-single-level"): the current
// level's name with its one-liner beneath, under the speedup digit.
export const SINGLE_LEVEL_READOUT_STYLE = {
  marginTopPx: 10,
  nameFontSizePx: 11,
  nameColor: "var(--accent)",
  descriptionFontSizePx: 9.5,
  descriptionMarginTopPx: 3,
  descriptionLineHeight: 1.45,
  // Reserved lines so level changes don't shift the column.
  descriptionSlotEm: 2.95,
  // The ink take ("level-dots"): the name in black instead of red.
  nameInkColor: "#111111",
  // "level-plain-spaced": extra air between the speedup block and the level
  // (18 read too low — Joanna 2026-07-15).
  spacedMarginTopPx: 13,
}

// ─── Tab 5 (was 89): the word-swap animation (wordSwapAnimation) ─────────────
// When a band-quantized readout changes under the sweeping cursor — the risk
// word stepping Low → Medium-Low, the agent level stepping Reliable Agent →
// Automated coder (and its one-liner with it) — the old word cuts out fast
// and the new one settles in on the default hover animation's clock, rising
// a few px as it lands, instead of the glyphs snapping in place. Continuous
// values (the 1.9x speedup) stay live-ticking, un-animated.
export const WORD_SWAP_ANIMATION = {
  outMs: 45,
  outEasing: "linear",
  inMs: 120,
  inEasing: "cubic-bezier(0.16, 1, 0.3, 1)",
  riseYPx: 3,
}

// Grid-stacks the outgoing and incoming text in one cell: the old node keeps
// its React key when the value changes, so its fade-out transitions from its
// current opacity; the new one mounts a frame at opacity 0, then eases in.
const AnimatedWordSwap = ({
  text,
  block = false,
}: {
  text: string
  // Block mode for multi-line text (the level one-liner) — the grid spans
  // the parent's width so the text wraps; inline mode hugs the word.
  block?: boolean
}) => {
  const anim = WORD_SWAP_ANIMATION
  const [shown, setShown] = useState(text)
  const [exiting, setExiting] = useState<string | null>(null)
  const [entered, setEntered] = useState(true)
  useEffect(() => {
    if (text === shown) return
    setExiting(shown)
    setShown(text)
    setEntered(false)
  }, [text, shown])
  // One frame at the start state so both transitions actually run: the
  // incoming word commits at opacity 0 (risen), the outgoing at 1.
  useEffect(() => {
    if (entered) return
    let flipped = false
    const flip = () => {
      if (flipped) return
      flipped = true
      setEntered(true)
    }
    const raf = requestAnimationFrame(flip)
    const timer = setTimeout(flip, 50)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(timer)
    }
  }, [entered])
  useEffect(() => {
    if (exiting === null) return
    const timer = setTimeout(() => setExiting(null), anim.outMs + 60)
    return () => clearTimeout(timer)
  }, [exiting, anim.outMs])
  return (
    <span className={block ? "grid" : "inline-grid"}>
      {exiting !== null && exiting !== shown && (
        <span
          key={exiting}
          style={{
            gridArea: "1 / 1",
            opacity: entered ? 0 : 1,
            transition: `opacity ${anim.outMs}ms ${anim.outEasing}`,
          }}
        >
          {exiting}
        </span>
      )}
      <span
        key={shown}
        style={{
          gridArea: "1 / 1",
          opacity: entered ? 1 : 0,
          transform: entered ? "none" : `translateY(${anim.riseYPx}px)`,
          transition: `opacity ${anim.inMs}ms ${anim.inEasing}, transform ${anim.inMs}ms ${anim.inEasing}`,
        }}
      >
        {shown}
      </span>
    </span>
  )
}

// "year-pinned": the cursor's year at the panel's top-right.
export const CLARITY_YEAR_BADGE = {
  prefix: "in ",
  fontSizePx: 10,
  color: "#111111",
}

// "risk-scale-dots": the word scale's six bands as a tiny strip beside the
// word — the risk counterpart of the level dots.
export const RISK_WORD_DOTS_STYLE = {
  marginLeftPx: 6,
  dotSizePx: 6,
  dotStrokeWidthPx: 1,
  dotGapPx: 2.5,
  color: "#8B0000",
  transitionCss: "background-color 200ms ease-out",
}

const RiskWordDotsRow = ({ fraction }: { fraction: number | null }) => {
  const style = RISK_WORD_DOTS_STYLE
  const currentIndex =
    fraction === null
      ? -1
      : RISK_WORD_SCALE.findIndex((band) => fraction < band.belowFraction)
  return (
    <span className="flex items-center" style={{ marginLeft: style.marginLeftPx }}>
      {RISK_WORD_SCALE.map((band, index) => (
        <span
          key={band.label}
          className="rounded-full"
          style={{
            width: style.dotSizePx,
            height: style.dotSizePx,
            marginRight: style.dotGapPx,
            border: `${style.dotStrokeWidthPx}px solid ${style.color}`,
            background: index === currentIndex ? style.color : "transparent",
            transition: style.transitionCss,
          }}
        />
      ))}
    </span>
  )
}

// The tiny seven-level strip (o-o-o-o-o-o-o): one dot per ladder level
// joined by hairline connectors, the current level solid, the rest outline.
export const LEVEL_DOTS_STYLE = {
  marginTopPx: 6,
  dotSizePx: 6,
  dotStrokeWidthPx: 1,
  // Bare dots, tightly spaced — the connector line came out
  // (Joanna 2026-07-15).
  dotGapPx: 2.5,
  color: "#111111",
  transitionCss: "background-color 200ms ease-out",
}

const LevelDotsRow = ({
  currentLabel,
  marginTopPx,
}: {
  currentLabel: string | null
  marginTopPx?: number
}) => {
  const style = LEVEL_DOTS_STYLE
  return (
    <div className="flex items-center" style={{ marginTop: marginTopPx ?? style.marginTopPx }}>
      {CAPABILITY_LEVEL_LADDER.map((level) => (
        <span
          key={level.label}
          className="rounded-full"
          style={{
            width: style.dotSizePx,
            height: style.dotSizePx,
            marginRight: style.dotGapPx,
            border: `${style.dotStrokeWidthPx}px solid ${style.color}`,
            background: level.label === currentLabel ? style.color : "transparent",
            transition: style.transitionCss,
          }}
        />
      ))}
    </div>
  )
}

const LADDER_COLUMN_CLARITY_DESIGNS: Record<
  LadderColumnClarityDesign,
  {
    // The header-bearing designs drop the big panel title — the column
    // headers already name both sides (Joanna 2026-07-15: redundant).
    title: boolean
    header: boolean
    // "title" renders the column headers at the panel title's own tier
    // (HudHoverTitle) instead of the small caps.
    headerTier?: "title"
    iconPlacement: "value" | "header"
    // Which digits carry a small label underneath.
    valueLabels: "both" | "left" | "none"
    riskLabelShort: boolean
    defs: boolean
    defFontSizePx?: number
    // How the capability column shows the agent level: the full list (76's
    // CompactLevelList) or a single current-level readout.
    levelDisplay?: "list" | "single"
    // "level-dots" extras: the capability definition sentence dropped, the
    // level name in ink instead of red, the seven-dot strip below.
    capabilityDef?: boolean
    levelNameBlack?: boolean
    levelDots?: boolean
    // Extra air above the level readout (spacedMarginTopPx).
    levelSpaced?: boolean
    // The 87 clarity fixes (see the id comments above).
    yearBadge?: boolean
    levelFirst?: boolean
    riskPercent?: boolean
    riskDotsScale?: boolean
    // Tab 89 (Joanna 2026-07-15): the "Takeover Risk" column header in red
    // (redHeaderColor in LADDER_COLUMN_CLARITY_STYLE).
    riskHeaderRed?: boolean
    // Tab 5 / was 89 (Joanna 2026-07-15): band changes (risk word, agent
    // level name + one-liner) animate — see WORD_SWAP_ANIMATION.
    wordSwapAnimation?: boolean
  }
> = {
  headers: { title: false, header: true, iconPlacement: "value", valueLabels: "left", riskLabelShort: false, defs: true },
  "labels-under": { title: true, header: false, iconPlacement: "value", valueLabels: "both", riskLabelShort: false, defs: true },
  "headers-no-defs": { title: false, header: true, iconPlacement: "value", valueLabels: "left", riskLabelShort: false, defs: false },
  "header-icon": { title: false, header: true, iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true },
  "value-first": { title: true, header: false, iconPlacement: "value", valueLabels: "both", riskLabelShort: true, defs: true, defFontSizePx: 9 },
  "big-headers": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true },
  "big-headers-single-level": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true, levelDisplay: "single" },
  "level-dots": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true, capabilityDef: false, levelDisplay: "single", levelNameBlack: true, levelDots: true },
  "level-plain-spaced": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true, capabilityDef: false, levelDisplay: "single", levelNameBlack: true, levelDots: false, levelSpaced: true },
  "year-pinned": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true, capabilityDef: false, levelDisplay: "single", levelNameBlack: true, levelDots: false, levelSpaced: true, yearBadge: true },
  "level-first": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true, capabilityDef: false, levelDisplay: "single", levelNameBlack: true, levelDots: false, levelSpaced: true, levelFirst: true, riskHeaderRed: true, wordSwapAnimation: true },
  "risk-percent": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true, capabilityDef: false, levelDisplay: "single", levelNameBlack: true, levelDots: false, levelSpaced: true, riskPercent: true },
  "risk-scale-dots": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true, capabilityDef: false, levelDisplay: "single", levelNameBlack: true, levelDots: false, levelSpaced: true, riskDotsScale: true },
  "clarity-all": { title: false, header: true, headerTier: "title", iconPlacement: "header", valueLabels: "left", riskLabelShort: false, defs: true, capabilityDef: false, levelDisplay: "single", levelNameBlack: true, levelDots: false, levelSpaced: true, yearBadge: true, levelFirst: true, riskPercent: true, riskDotsScale: true },
}

// ─── Tab 19's INFO-LAYOUT variants (tabs 55–59, combinedInfoLayout) ──────────
// How the combined explainer organizes the same information (title, the
// combined definition, the two stats, the ladder):
//   "stats-row"           — title and definition run FULL-WIDTH; the two
//                           stats sit in a two-column row between the
//                           definition and the ladder
//   "risk-below"          — each number lives with its section: the speedup
//                           stays in the header, the risk drops BELOW the
//                           ladder as its own line
//   "labeled-description" — the dense definition splits into two labeled
//                           micro-sentences ("Line — …" / "Risk — …")
//   "stat-cells"          — the stats as HUD-style cells (bold value on
//                           top, small label under) in a row under the
//                           title, definition below
//   "description-last"    — the prose demotes to fine print BELOW the
//                           ladder; the header is just title + stats
//   "level-cells"         — tab 58 plus the capability LEVEL leading the
//                           cell row ("Superhuman AI Researcher" as the
//                           first, primary cell), the definition demoted to
//                           smaller type below the cells
//   "level-cells-fixed"   — tab 60 with FIXED cell columns: the level cell
//                           reserves the longest name's width, so nothing
//                           shifts as the level changes under the cursor
//   "level-cells-tail"    — tab 60 with the numbers FIRST and the level
//                           name last, where its varying width pushes
//                           nothing
//   "cells-icons"         — tab 58 with the title "AI Capabilities and
//                           Risk" and an icon beside each metric (the
//                           capability-line glyph / a red dither swatch)
//   "cells-def-columns"   — tab 58 with each part's definition BELOW its
//                           cell, one column each
//   "cells-def-columns-icons" — tab 64 with an icon beside each big digit
//                           and the small titles above them removed
//   "cells-def-columns-three" — all THREE things in a row (agent level,
//                           R&D speedup, takeover risk), one column each
//                           with its definition below; the level column's
//                           definition is the hovered level's own one-liner
//   "cells-def-columns-ladder-in-column" — tab 65 with the agent levels
//                           living INSIDE the capability column (names
//                           only, gliding mark) and a single reserved slot
//                           showing just the ACTIVE level's one-liner — no
//                           full-width ladder below
//   "level-cells-defs"    — tab 74's SHORT panel: tab 61's top (title +
//                           the level/speedup/risk cells) with each cell's
//                           definition directly beneath it as THREE equal
//                           columns — and NO ladder (on tab 74 the agent
//                           table lives on the labor hover instead, and the
//                           takeover only covers the policies/power row)
export type CombinedInfoLayoutId =
  | "stock"
  | "stats-row"
  | "risk-below"
  | "labeled-description"
  | "stat-cells"
  | "description-last"
  | "level-cells"
  | "level-cells-fixed"
  | "level-cells-tail"
  | "cells-icons"
  | "cells-def-columns"
  | "cells-def-columns-icons"
  | "cells-def-columns-three"
  | "cells-def-columns-ladder-in-column"
  | "level-cells-defs"

// "cells-icons" swaps the panel title for this.
export const COMBINED_CELLS_ICONS_TITLE = "AI Capabilities and Risk"

// "cells-def-columns": each metric's definition, one column under its cell.
export const CELLS_DEF_COLUMNS_COPY = {
  capability: "How powerful we predict AI will be at this time.",
  // Joanna's 2026-07-15 phrasing (was "escape human control … that year's
  // alignment techniques").
  risk: "Our rough estimate of how likely AI with that capability is to disempower humanity, given alignment techniques at that time. The denser the red dots, the greater the risk.",
  // The three-column layout's middle column (the level column's definition
  // is the hovered level's own ladder one-liner).
  speedupThree: "How much faster AI makes AI research go.",
}

// The WORD scale for the risk value (riskCombinedRiskWord): the percentage
// stays the underlying number; the display maps it through these bands
// (Joanna's authored bands, 2026-07-15):
//   Low         <15%
//   Medium-Low  15–25%
//   Medium      25–35%
//   Medium-High 35–45%
//   High        45–55%
//   Very High   55%+
export const RISK_WORD_SCALE: { label: string; belowFraction: number }[] = [
  { label: "Low", belowFraction: 0.15 },
  { label: "Medium-Low", belowFraction: 0.25 },
  { label: "Medium", belowFraction: 0.35 },
  { label: "Medium-High", belowFraction: 0.45 },
  { label: "High", belowFraction: 0.55 },
  { label: "Very High", belowFraction: Infinity },
]
export const riskWordForFraction = (fraction: number): string => {
  for (const band of RISK_WORD_SCALE) {
    if (fraction < band.belowFraction) return band.label
  }
  return RISK_WORD_SCALE[RISK_WORD_SCALE.length - 1].label
}

// "level-cells-defs" (tab 74): one definition under each of the three cells.
// DRAFT copy — edit freely.
export const LEVEL_CELLS_DEFS_COPY = {
  level: "How powerful we predict AI will be at this time.",
  speedup:
    "How much faster AI research and development moves with AI doing the work, compared to human researchers alone.",
  risk: "How likely AI with that capability is to escape human control, given that year's alignment techniques.",
}

export const COMBINED_LABELED_DESCRIPTION = {
  line: { label: "Line", text: "The projected level of AI capability." },
  risk: {
    label: "Risk",
    text: "How likely AI with that capability is to escape human control, given that year's alignment techniques.",
  },
}

export const COMBINED_INFO_LAYOUT_STYLE = {
  statsRowMarginTopPx: 8,
  statsRowGapPx: 24,
  statCellValueFontSizePx: 13,
  statCellLabelFontSizePx: 8.5,
  labeledDescFontSizePx: 11,
  labeledDescLineHeight: 1.45,
  labeledDescLabelWidthPx: 34,
  descriptionLastFontSizePx: 10,
  descriptionLastMarginTopPx: 10,
  // "level-cells"
  levelCellLabel: "Capability Level",
  cellsDescriptionFontSizePx: 10,
  cellsDescriptionMarginTopPx: 8,
  // "level-cells-fixed": reserved column widths — the level cell fits the
  // longest name ("Top-Expert-Dominating AI") so the row never shifts.
  levelCellFixedWidthPx: 196,
  speedupCellFixedWidthPx: 108,
  // "cells-icons"
  cellIconGapPx: 6,
  // "cells-def-columns"
  defColumnsGapPx: 24,
  defColumnsFontSizePx: 10,
  defColumnsLineHeight: 1.45,
  defColumnsMarginTopPx: 6,
  riskBelowMarginTopPx: 10,
  riskBelowFontSizePx: 11,
}

// ─── The two-key footer (tab 40, riskKeysFooter) ─────────────────────────────
// Two columns at the panel's bottom, one key per dither field: the red
// takeover-risk field and the black delayed-(trailing-)actor field, each
// with a small dither swatch showing which dots it means. DRAFT copy — the
// black field's semantics per trailingActorRiskBand.ts (how far a project
// racing OUTSIDE the slowdown deal has probably gotten).
export const RISK_KEYS_FOOTER_COPY = {
  takeover: {
    title: "Takeover Risk",
    // Joanna's 2026-07-14 phrasing (also drops the "The red dots:" lead —
    // the swatch shows which dots).
    text: "How likely AI with that capability is to escape human control, given that year's alignment techniques.",
  },
  delayedActor: {
    title: "Trailing Actor Risk",
    text: "How likely a non-deal actor has reached the frontier at this point, e.g. because the deal broke down.",
  },
}

export const RISK_KEYS_FOOTER_STYLE = {
  marginTopPx: 12,
  columnGapPx: 18,
  titleFontSizePx: 9,
  textFontSizePx: 9.5,
  textLineHeight: 1.45,
  textMarginTopPx: 3,
  swatchTitleGapPx: 6,
  // The swatch's dot grid: columns × rows, radius per row (top → bottom).
  // The red field is dense at its top edge, the black at its bottom — the
  // rows mirror that.
  swatchDotColumns: 7,
  swatchDotSpacingPx: 4.5,
  swatchRowRadiiRed: [1.6, 1.25, 0.85, 0.45],
  swatchRowRadiiBlack: [0.45, 0.85, 1.25, 1.6],
  swatchRedColor: "#8B0000",
  swatchBlackColor: "#1a1a1a",
}

// A tiny dither-field swatch: a dot grid whose row radii fade like the real
// field's density gradient.
const DitherKeySwatch = ({
  color,
  rowRadii,
}: {
  color: string
  rowRadii: number[]
}) => {
  const style = RISK_KEYS_FOOTER_STYLE
  const width = style.swatchDotColumns * style.swatchDotSpacingPx
  const height = rowRadii.length * style.swatchDotSpacingPx
  return (
    <svg width={width} height={height} className="shrink-0" aria-hidden>
      {rowRadii.map((radius, rowIndex) =>
        Array.from({ length: style.swatchDotColumns }, (_, columnIndex) => (
          <circle
            key={`${rowIndex}-${columnIndex}`}
            cx={(columnIndex + 0.5) * style.swatchDotSpacingPx}
            cy={(rowIndex + 0.5) * style.swatchDotSpacingPx}
            r={radius}
            fill={color}
          />
        )),
      )}
    </svg>
  )
}

// ─── Tab 40's HIERARCHY variants (tabs 41–45, riskCombinedHierarchy) ─────────
// How tab 40's panel (capabilities section + two-key section) separates its
// levels so the eye finds the structure:
//   "rule"          — a dotted rule between the sections (the HUD's
//                     hover-separator grammar): hierarchy by separation
//   "type-scale"    — the capability title UP a size, the key titles DOWN
//                     one: hierarchy by type contrast
//   "icon"          — a small capability-line glyph (black rising curve,
//                     red endpoint dot — the graph in miniature) beside the
//                     title + subtitle, so BOTH sections lead with a
//                     pictogram: hierarchy by consistent iconography
//   "keys-footnote" — the keys shrink to a footnote row (smaller swatches
//                     and type): hierarchy by demotion
//   "keys-run-in"   — each key is one flowing line (swatch, bold run-in
//                     title, dash, text): hierarchy by structure — the keys
//                     read as annotations, not sections
//   "icon-rule"       — Joanna's 2026-07-14 combo: 43's capability-line icon
//                       + 41's dotted rule + 44's SMALLER key swatches (the
//                       key text stays stock size)
//   "title-icon-rule" — the combo, but the icon sits inline with the TITLE
//                       line only (small, title-height), not spanning the
//                       subtitle
//   "title-icon-rule-run-in" — tab 52 plus 45's flowing keys, separated by
//                       a COLON ("TAKEOVER RISK: the red dots…") instead of
//                       the dash
//   "rule-run-in"     — tab 53 without the capability-line icon: plain
//                       title, rule, colon run-in keys
export type CombinedHierarchyId =
  | "stock"
  | "rule"
  | "type-scale"
  | "icon"
  | "keys-footnote"
  | "keys-run-in"
  | "icon-rule"
  | "title-icon-rule"
  | "title-icon-rule-run-in"
  | "rule-run-in"

export const COMBINED_HIERARCHY_STYLE = {
  // "rule" — the line itself is HudHoverDetailRule (the tab's hover-separator
  // treatment, e.g. "black-dotted" in HUD_HOVER_RULE_TREATMENTS,
  // hudHoverDetailHeader.tsx); only the air around it is set here.
  ruleGapAbovePx: 16,
  ruleGapBelowPx: 10,
  // "type-scale"
  scaledTitleFontSizePx: 14,
  scaledSubtitleFontSizePx: 11,
  // "icon"
  iconSizePx: 26,
  iconGapPx: 9,
  iconStrokeWidthPx: 1.8,
  iconColor: "#111111",
  iconDotRadiusPx: 2.4,
  iconDotColor: "#8B0000",
  // "title-icon-rule": the icon at title-line height, inline before the
  // title text. Drawn with its OWN geometry (not the 26px glyph scaled
  // down, which thinned the stroke to ~1px and muddied the curve): a
  // simpler bend, a relatively thicker stroke, and a bigger endpoint dot,
  // all in a 14-unit viewBox so the values below are real pixels.
  titleIconSizePx: 14,
  titleIconGapPx: 5,
  titleIconPath: "M 1 12.5 C 6 12.5, 8.5 9.5, 11.5 2.5",
  titleIconStrokeWidthPx: 1.6,
  titleIconDotRadiusPx: 1.9,
  titleIconDotX: 11.5,
  titleIconDotY: 2.5,
  // "keys-footnote"
  footnoteTitleFontSizePx: 8,
  footnoteTextFontSizePx: 8.5,
  footnoteSwatchRowRadiiRed: [1.3, 0.9, 0.5],
  footnoteSwatchRowRadiiBlack: [0.5, 0.9, 1.3],
}

// The capability-line glyph ("icon"): the graph in miniature — a black
// rising curve with the red current-point dot at its end.
const CapabilityLineIcon = () => {
  const style = COMBINED_HIERARCHY_STYLE
  const resolvedSize = style.iconSizePx
  return (
    <svg
      width={resolvedSize}
      height={resolvedSize}
      viewBox="0 0 26 26"
      className="shrink-0"
      aria-hidden
    >
      <path
        d="M 2 22 C 10 22, 15 18, 21 6"
        fill="none"
        stroke={style.iconColor}
        strokeWidth={style.iconStrokeWidthPx}
        strokeLinecap="round"
      />
      <circle cx={21} cy={6} r={style.iconDotRadiusPx} fill={style.iconDotColor} />
    </svg>
  )
}

// The title-height version of the glyph (tabs 52–53) — its own drawing,
// tuned to stay crisp at 14px; see the titleIcon* knobs above.
const CapabilityLineIconSmall = ({ size }: { size?: number } = {}) => {
  const style = COMBINED_HIERARCHY_STYLE
  const resolvedSize = size ?? style.titleIconSizePx
  return (
    <svg width={resolvedSize} height={resolvedSize} viewBox="0 0 14 14" className="shrink-0" aria-hidden>
      <path
        d={style.titleIconPath}
        fill="none"
        stroke={style.iconColor}
        strokeWidth={style.titleIconStrokeWidthPx}
        strokeLinecap="round"
      />
      <circle
        cx={style.titleIconDotX}
        cy={style.titleIconDotY}
        r={style.titleIconDotRadiusPx}
        fill={style.iconDotColor}
      />
    </svg>
  )
}

// How the keys row renders per hierarchy — see RiskKeysFooter.
// "small-swatches": 44's shrunken swatches with STOCK title/text sizes.
// "run-in-colon": the run-in line with small swatches and a colon separator.
type RiskKeysFooterMode = "stock" | "footnote" | "run-in" | "small-titles" | "small-swatches" | "run-in-colon"

// The footer itself: two equal columns, swatch + title on one line, the
// explanation under them.
const RiskKeysFooter = ({ mode = "stock" }: { mode?: RiskKeysFooterMode }) => {
  const style = RISK_KEYS_FOOTER_STYLE
  const hierarchy = COMBINED_HIERARCHY_STYLE
  const footnote = mode === "footnote"
  const smallSwatches = footnote || mode === "small-swatches" || mode === "run-in-colon"
  const runIn = mode === "run-in" || mode === "run-in-colon"
  const keys = [
    {
      ...RISK_KEYS_FOOTER_COPY.takeover,
      color: style.swatchRedColor,
      rowRadii: smallSwatches ? hierarchy.footnoteSwatchRowRadiiRed : style.swatchRowRadiiRed,
    },
    {
      ...RISK_KEYS_FOOTER_COPY.delayedActor,
      color: style.swatchBlackColor,
      rowRadii: smallSwatches ? hierarchy.footnoteSwatchRowRadiiBlack : style.swatchRowRadiiBlack,
    },
  ]
  const titleFontSizePx = footnote
    ? hierarchy.footnoteTitleFontSizePx
    : mode === "small-titles"
      ? hierarchy.footnoteTitleFontSizePx
      : style.titleFontSizePx
  const textFontSizePx = footnote ? hierarchy.footnoteTextFontSizePx : style.textFontSizePx
  return (
    <div
      className="flex"
      style={{ marginTop: style.marginTopPx, gap: style.columnGapPx }}
    >
      {keys.map((key) => (
        <div key={key.title} className="min-w-0 flex-1">
          {runIn ? (
            // One flowing line per key: swatch, bold run-in title, dash,
            // text — the keys read as annotations, not sections.
            <div
              className="font-sans"
              style={{ fontSize: textFontSizePx, lineHeight: style.textLineHeight }}
            >
              <span
                className="mr-1.5 inline-block align-middle"
                style={{ marginTop: -2 }}
              >
                <DitherKeySwatch color={key.color} rowRadii={key.rowRadii} />
              </span>
              <b
                className="mr-1 font-mono uppercase tracking-wider"
                style={{ fontSize: titleFontSizePx }}
              >
                {key.title}
                {mode === "run-in-colon" && ":"}
              </b>
              {mode === "run-in-colon" ? "" : "— "}
              {key.text}
            </div>
          ) : (
            <>
              <div className="flex items-center" style={{ gap: style.swatchTitleGapPx }}>
                <DitherKeySwatch color={key.color} rowRadii={key.rowRadii} />
                <span
                  className="font-mono font-bold uppercase tracking-wider"
                  style={{ fontSize: titleFontSizePx }}
                >
                  {key.title}
                </span>
              </div>
              <div
                className="font-sans"
                style={{
                  marginTop: style.textMarginTopPx,
                  fontSize: textFontSizePx,
                  lineHeight: style.textLineHeight,
                }}
              >
                {key.text}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  )
}

// The Top-Expert-Dominating definition hints (riskHoverTedDefinition) — the
// subtitle names the TED capability level, and these are the quiet ways of
// saying what it MEANS (the capability ladder's one-liner) without
// dominating the explainer. "inline" swaps the subtitle for the
// definition-woven variant below; "footnote" appends a small labeled line
// under the subtitle. (A third, in-canvas take rides the foom readout — see
// RISK_FOOM_TED_CAPTION in RiskFoomPreviewOverlay.tsx.)
// "risk-line" (tab 29): the definition rides INSIDE the ted-line label
// itself — see TED_RISK_LINE_LABEL_WITH_DEFINITION.
export type RiskHoverTedDefinition = "inline" | "footnote" | "risk-line"

export const TED_RISK_LINE_LABEL_WITH_DEFINITION =
  "Risk if Top-Expert-Dominating AI level — outperforming the top human expert in nearly every field — was achieved this year:"

// ─── The ted-line panel's SKIMMABILITY designs (tabs 35–39) ──────────────────
// How the ted-line takeover lays out the same content so a skimmer catches
// it (riskTedPanelDesign; "stock" = tab 26's layout):
//   "big-number"    — the % LEADS as a large red figure with a small label,
//                     the definition demoted below it
//   "phrase-header" — the % rides the header as the "80% in 2027" phrase,
//                     a small key line under the definition explains it
//   "bold-keywords" — tab 26's layout, the definition's load-bearing phrases
//                     BOLDED so a skim catches the skeleton
//   "stat-row"      — the % as an R&D-speedup-style stat row directly under
//                     the title, definition below
//   "terse"         — rewritten copy: one short claim with the % inline,
//                     the caveats demoted to a small method note
export type RiskTedPanelDesignId =
  | "stock"
  | "big-number"
  | "phrase-header"
  | "bold-keywords"
  | "stat-row"
  | "terse"

export const RISK_TED_PANEL_COPY = {
  bigNumberLabel: "risk if Top-Expert-Dominating AI arrived this year",
  phraseKeyLine: "= risk if Top-Expert-Dominating AI level arrived that year",
  statRowLabel: "Risk if Top-Expert-Dominating AI arrived this year",
  terseLeadPrefix: "If AI reached Top-Expert-Dominating level this year: ",
  terseLeadSuffix: " chance it disempowers humanity.",
  terseMethodNote:
    "Our extremely rough guess, assuming that year's alignment techniques. The denser the dots, the higher the risk.",
}

// The definition with its load-bearing phrases bolded ("bold-keywords").
export const TAKEOVER_RISK_DESCRIPTION_BOLD_KEYWORDS = (
  <>
    Our extremely rough guess on <b>how likely AI is to disempower humanity</b> if
    scaled rapidly to <b>Top-Expert-Dominating</b> level of capabilities, with{" "}
    <b>that year&apos;s alignment techniques</b>. The denser the dots, the higher
    the risk.
  </>
)

export const RISK_TED_PANEL_STYLE = {
  bigNumberFontSizePx: 30,
  bigNumberColor: "var(--accent)",
  bigNumberLabelFontSizePx: 10,
  bigNumberMarginTopPx: 8,
  statValueFontSizePx: 15,
  keyLineFontSizePx: 9.5,
  keyLineMarginTopPx: 5,
  methodNoteFontSizePx: 10,
  methodNoteMarginTopPx: 6,
  terseLeadFontSizePx: 14,
}

// The skimmability designs render the WHOLE ted-line takeover themselves
// ("stock" keeps TakeoverRiskAreaDetail's shared path, so tabs 26–33 are
// untouched baselines).
const TedLinePanel = ({
  nowValue,
  currentYear,
}: {
  nowValue: number | null
  currentYear: number
}) => {
  const swap = useCapabilityHoverSwap()
  const design = swap?.riskTedPanelDesign ?? "stock"
  const style = RISK_TED_PANEL_STYLE
  const copy = RISK_TED_PANEL_COPY
  const value = nowValue === null ? null : fmtLikelihood(nowValue)
  const description =
    design === "bold-keywords"
      ? TAKEOVER_RISK_DESCRIPTION_BOLD_KEYWORDS
      : TAKEOVER_RISK_HOVER_COPY.description
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <HudHoverTitle>{TAKEOVER_RISK_HOVER_COPY.title}</HudHoverTitle>
        {design === "phrase-header" && value !== null && (
          <span
            className="flex shrink-0 items-baseline whitespace-nowrap font-mono"
            style={{ fontSize: RISK_HOVER_DETAIL_STYLE.nowPhraseFontSize }}
          >
            <b style={{ color: RISK_HOVER_DETAIL_STYLE.nowPhraseValueColor }}>{value}</b>
            <span
              style={{
                marginLeft: RISK_HOVER_DETAIL_STYLE.nowPhraseWordGapPx,
                marginRight: RISK_HOVER_DETAIL_STYLE.nowPhraseWordGapPx,
              }}
            >
              in
            </span>
            <b>{Math.floor(currentYear)}</b>
          </span>
        )}
      </div>
      {design === "big-number" && value !== null && (
        <div style={{ marginTop: style.bigNumberMarginTopPx }}>
          <div
            className="font-mono font-bold leading-none"
            style={{ fontSize: style.bigNumberFontSizePx, color: style.bigNumberColor }}
          >
            {value}
          </div>
          <div
            className="mt-1 font-mono"
            style={{ fontSize: style.bigNumberLabelFontSizePx }}
          >
            {copy.bigNumberLabel}
          </div>
        </div>
      )}
      {design === "stat-row" && value !== null && (
        <div
          className="flex items-baseline justify-between gap-3 font-mono"
          style={{ marginTop: COMBINED_RISK_LINE_STYLE.marginTopPx }}
        >
          <span className="text-[10px]">{copy.statRowLabel}</span>
          <span
            className="font-bold whitespace-nowrap"
            style={{
              fontSize: style.statValueFontSizePx,
              color: COMBINED_RISK_LINE_STYLE.valueColor,
            }}
          >
            {value}
          </span>
        </div>
      )}
      {design === "terse" ? (
        <>
          {value !== null && (
            <HudHoverDescription>
              <span style={{ fontSize: style.terseLeadFontSizePx }}>
                {copy.terseLeadPrefix}
                <b style={{ color: COMBINED_RISK_LINE_STYLE.valueColor }}>{value}</b>
                {copy.terseLeadSuffix}
              </span>
            </HudHoverDescription>
          )}
          <div
            className="font-sans"
            style={{
              marginTop: style.methodNoteMarginTopPx,
              fontSize: style.methodNoteFontSizePx,
              lineHeight: 1.45,
            }}
          >
            {copy.terseMethodNote}
          </div>
        </>
      ) : (
        <HudHoverDescription>{description}</HudHoverDescription>
      )}
      {design === "phrase-header" && (
        <div
          className="font-mono"
          style={{ marginTop: style.keyLineMarginTopPx, fontSize: style.keyLineFontSizePx }}
        >
          {copy.phraseKeyLine}
        </div>
      )}
      {design === "bold-keywords" && nowValue !== null && (
        <div
          className="font-mono"
          style={{
            marginTop: COMBINED_RISK_LINE_STYLE.marginTopPx,
            fontSize: COMBINED_RISK_LINE_STYLE.fontSize,
          }}
        >
          {COMBINED_RISK_LINE_COPY.tedThisYearLabel}
          <b
            style={{
              marginLeft: COMBINED_RISK_LINE_STYLE.labelValueGapPx,
              color: COMBINED_RISK_LINE_STYLE.valueColor,
            }}
          >
            {fmtLikelihood(nowValue)}
          </b>
        </div>
      )}
    </div>
  )
}

export const TAKEOVER_RISK_HOVER_DESCRIPTION_TED_INLINE =
  "Our extremely rough guess on how likely AI is to disempower humanity if scaled rapidly to Top-Expert-Dominating level of capabilities — AI that outperforms the top human expert in nearly every field — with that year's alignment techniques. The denser the dots, the higher the risk."

export const TAKEOVER_RISK_TED_FOOTNOTE = {
  label: "Top-Expert-Dominating AI",
  text: "Outperforms the top human expert in nearly every field.",
  labelFontSizePx: 8.5,
  textFontSizePx: 9.5,
  marginTopPx: 5,
  lineHeight: 1.45,
}

// The capability ladder shown in the takeover, lowest to highest. `label`
// must match capabilityThresholds.tsx labels exactly — that's how the hovered
// point's level is bolded. Descriptions are the layperson one-liners.
export const CAPABILITY_LEVEL_LADDER: { label: string; description: string }[] = [
  {
    label: "Unreliable Agent",
    description:
      "Can handle long tasks, but fails often without oversight.",
  },
  {
    label: "Reliable Agent",
    description: "Can handle long tasks without constant human checking.",
  },
  {
    label: "Automated coder",
    description: "Handles nearly all software engineering tasks end to end.",
  },
  {
    label: "Superhuman AI Researcher",
    description: "Better at AI research than the best human researchers.",
  },
  {
    label: "Top-Expert-Dominating AI",
    description: "Outperforms the top human expert in nearly every field.",
  },
  {
    label: "Superintelligent",
    description: "Far beyond human capability across the board.",
  },
  {
    label: "Wildly Superintelligent",
    description: "Qualitatively beyond human science and comprehension.",
  },
]

// Type treatment for the takeover's ladder rows — the HUD's usual voice:
// mono for labels and values, sans for explanations, bold (not color) marks
// the current level. (The title/subtitle above the ladder render in the
// shared HudHoverTitle / HudHoverDescription opener.)
export const CAPABILITY_HOVER_STYLE = {
  ladderRowFontSize: "9.5px",
  ladderRowLineHeight: 1.6,
}

type CapabilityHoverSwapContextValue = {
  hoveredPoint: PointHoverTooltipPayload | null
  setHoveredPoint: (point: PointHoverTooltipPayload | null) => void
  // The pointer is in the takeover-risk field above the line (see
  // takeoverRiskAreaHover in extrapolated-graph/types.ts) — mutually
  // exclusive with hoveredPoint; the target shows TAKEOVER_RISK_HOVER_COPY.
  hoveredTakeoverRiskArea: boolean
  setHoveredTakeoverRiskArea: (hovered: boolean) => void
  // The cursor-driven (fractional) year while the risk field is hovered
  // (see riskAreaHoverYear on the band channel); null otherwise.
  hoveredRiskAreaYear: number | null
  setHoveredRiskAreaYear: (year: number | null) => void
  // The 2D risk-space explorer's live readout (see riskSpaceReadout on the
  // band channel) — the "space-ladder" takeover renders from it.
  hoveredRiskSpaceReadout: { levelLabel: string; riskFraction: number } | null
  setHoveredRiskSpaceReadout: (
    readout: { levelLabel: string; riskFraction: number } | null,
  ) => void
  // Which visual the risk-area takeover appends below the title + subtitle
  // — see RISK_HOVER_DETAIL_VISUALS. Unset keeps the copy-only stock.
  riskHoverDetail?: RiskHoverDetailVisual
  // How the takeover hints at what Top-Expert-Dominating MEANS — see
  // RiskHoverTedDefinition above. Unset = no hint.
  riskHoverTedDefinition?: RiskHoverTedDefinition
  // Tab 32: the ted-line takeover appends the FULL capability ladder (every
  // level with its one-liner), with the Top-Expert-Dominating row pinned as
  // the highlighted one — all the levels defined, that one marked.
  riskHoverCapabilityLadder?: boolean
  // Tabs 35–39: the ted-line takeover's skimmability layout — see
  // RiskTedPanelDesignId. Unset/"stock" keeps tab 26's layout.
  riskTedPanelDesign?: RiskTedPanelDesignId
  // The combined hover's explainer (tabs 14/19/21): the point-hover
  // takeover renders COMBINED_CAPABILITY_RISK_HOVER_COPY with both the
  // speedup and the risk stat instead of the capability-only header. The
  // value picks the risk semantics — see CombinedRiskSemantics. "foom"
  // pairs with the graph's riskFoomOnPointHover.
  combinedRiskDetail?: CombinedRiskSemantics
  // Tabs 22–24: the stacked capabilities-then-risk layout — see
  // CombinedRiskLayout / CombinedRiskLine.
  combinedRiskLayout?: CombinedRiskLayout
  combinedRiskLine?: CombinedRiskLine
  // Tab 40: the two-key footer (takeover / delayed-actor dither swatches)
  // at the stacked layout's bottom — see RISK_KEYS_FOOTER_COPY.
  riskKeysFooter?: boolean
  // Tabs 41–45: how tab 40's panel separates its levels — see
  // CombinedHierarchyId.
  combinedHierarchyDesign?: CombinedHierarchyId
  // Tabs 55–59: how tab 19's combined explainer organizes its information —
  // see CombinedInfoLayoutId.
  combinedInfoLayout?: CombinedInfoLayoutId
  // Tab 72: the risk value displays as the WORD scale instead of the
  // percentage — see RISK_WORD_SCALE.
  combinedRiskWord?: boolean
  // Tabs 66–68, 70 (the zone-clarity round): crossing the line/field zone
  // boundary runs the panel handoff on the hover-swap fade clock (the old
  // explainer out, the new one in on the stagger) instead of hard-swapping
  // content in one frame — see CapabilityHoverSwapTarget's contentKey.
  zonePanelFade?: boolean
  // Tab 69: BOTH zones show the same stacked capabilities + takeover-risk
  // panel and the hovered zone's section takes the red emphasis — crossing
  // the boundary glides the emphasis instead of swapping content. See
  // ZONE_EMPHASIS_PANEL_STYLE / ZoneEmphasisStackedDetail.
  zoneStackedEmphasis?: boolean
  // Tab 75: tab 69's stable-panel idea in tab 65's TWO-COLUMN grammar —
  // both zones show the same two metric columns and the hovered zone's
  // column takes the focus (gliding red bar + red name). See
  // ZONE_COLUMNS_PANEL_STYLE / ZoneEmphasisColumnsDetail.
  zoneColumnsEmphasis?: boolean
  // Tab 77: the zone-columns panel appends 76's agent-level list (names +
  // gliding mark + single active one-liner), tracking the cursor's year.
  zoneColumnsLevelList?: boolean
  // Tab 76's clarity variants — see LadderColumnClarityDesign.
  ladderColumnClarityDesign?: LadderColumnClarityDesign
  // Tab 87: the takeover detail starts FLUSH with the covered row's top
  // edge (drops the TAKEOVER_DETAIL_TOP_NUDGE_PX) — the headers sit at the
  // same vertical level the dist-of-power treemap's edge was at.
  takeoverDetailFlushTop?: boolean
  // Tab 34 (2026-07-15 refinement): the red dither swatch inline with the
  // "AI Takeover Risk" title in the field hover's panel — the field's icon
  // next to its name.
  riskHoverTitleSwatch?: boolean
  ladderRowHighlightDisabled: boolean
  ladderHighlight?: CapabilityLadderHighlight
  ladderDescriptionLayout?: CapabilityLadderDescriptionLayout
}

const CapabilityHoverSwapContext =
  createContext<CapabilityHoverSwapContextValue | null>(null)

// Null outside a <CapabilityHoverSwap>. AboveGlobeBandHudOverlay reads this to
// suppress the floating point-tooltip box (the dot + guide line stay), and
// CapabilityHoverSwapTarget reads it to render the takeover.
export const useCapabilityHoverSwap = () => useContext(CapabilityHoverSwapContext)

// Renders no DOM of its own. Must wrap BOTH the globe/graph block (so the
// bridge and tooltip suppression can see it) and the lower HUD section (the
// target). ladderRowHighlightDisabled drops the gray pill behind the
// takeover ladder's current row — the current level then reads from the red
// dot + bold name alone.
export const CapabilityHoverSwap = ({
  children,
  ladderRowHighlightDisabled = false,
  ladderHighlight,
  ladderDescriptionLayout,
  riskHoverDetail,
  riskHoverTedDefinition,
  riskHoverCapabilityLadder,
  riskTedPanelDesign,
  combinedRiskDetail,
  combinedRiskLayout,
  combinedRiskLine,
  riskKeysFooter,
  combinedHierarchyDesign,
  combinedInfoLayout,
  combinedRiskWord,
  zonePanelFade,
  zoneStackedEmphasis,
  zoneColumnsEmphasis,
  zoneColumnsLevelList,
  ladderColumnClarityDesign,
  takeoverDetailFlushTop,
  riskHoverTitleSwatch,
}: {
  children: ReactNode
  ladderRowHighlightDisabled?: boolean
  // Red current-row treatment for the takeover ladder — see
  // CAPABILITY_LADDER_HIGHLIGHT_STYLE. Unset keeps the stock gray pill.
  ladderHighlight?: CapabilityLadderHighlight
  // Description-cell layout — see CAPABILITY_LADDER_DESCRIPTION_LAYOUT_STYLE.
  ladderDescriptionLayout?: CapabilityLadderDescriptionLayout
  // The risk-area takeover's visual — see RISK_HOVER_DETAIL_VISUALS. Unset
  // keeps the title + subtitle stock.
  riskHoverDetail?: RiskHoverDetailVisual
  // The Top-Expert-Dominating definition hint — see RiskHoverTedDefinition.
  riskHoverTedDefinition?: RiskHoverTedDefinition
  // The ted-line takeover's full capability ladder, TED row pinned — see
  // the context field of the same name.
  riskHoverCapabilityLadder?: boolean
  // The ted-line takeover's skimmability layout — see the context field.
  riskTedPanelDesign?: RiskTedPanelDesignId
  // The combined hover's explainer — see the context field of the same name.
  combinedRiskDetail?: CombinedRiskSemantics
  // The stacked layout + its risk line — see the context fields.
  combinedRiskLayout?: CombinedRiskLayout
  combinedRiskLine?: CombinedRiskLine
  // The two-key footer — see the context field.
  riskKeysFooter?: boolean
  // Tab 40's hierarchy variant — see the context field.
  combinedHierarchyDesign?: CombinedHierarchyId
  // Tab 19's info layout — see the context field.
  combinedInfoLayout?: CombinedInfoLayoutId
  // The risk-as-word display — see the context field.
  combinedRiskWord?: boolean
  // The zone-crossing panel fade (tabs 66–68, 70) — see the context field.
  zonePanelFade?: boolean
  // The stable two-section panel (tab 69) — see the context field.
  zoneStackedEmphasis?: boolean
  // The two-column focus panel (tab 75) — see the context field.
  zoneColumnsEmphasis?: boolean
  // Tab 77: the zone-columns panel appends 76's agent-level list (names +
  // gliding mark + single active one-liner), tracking the cursor's year.
  zoneColumnsLevelList?: boolean
  // Tab 76's clarity variants — see LadderColumnClarityDesign.
  ladderColumnClarityDesign?: LadderColumnClarityDesign
  // Tab 87: the takeover detail starts FLUSH with the covered row's top
  // edge (drops the TAKEOVER_DETAIL_TOP_NUDGE_PX) — the headers sit at the
  // same vertical level the dist-of-power treemap's edge was at.
  takeoverDetailFlushTop?: boolean
  // The title's red dither swatch (tab 34) — see the context field.
  riskHoverTitleSwatch?: boolean
}) => {
  const [hoveredPoint, setHoveredPoint] =
    useState<PointHoverTooltipPayload | null>(null)
  const [hoveredTakeoverRiskArea, setHoveredTakeoverRiskArea] = useState(false)
  const [hoveredRiskAreaYear, setHoveredRiskAreaYear] = useState<number | null>(null)
  const [hoveredRiskSpaceReadout, setHoveredRiskSpaceReadout] = useState<{
    levelLabel: string
    riskFraction: number
  } | null>(null)
  const value = useMemo(
    () => ({
      hoveredPoint,
      setHoveredPoint,
      hoveredTakeoverRiskArea,
      setHoveredTakeoverRiskArea,
      hoveredRiskAreaYear,
      setHoveredRiskAreaYear,
      hoveredRiskSpaceReadout,
      setHoveredRiskSpaceReadout,
      riskHoverDetail,
      riskHoverTedDefinition,
      riskHoverCapabilityLadder,
      riskTedPanelDesign,
      combinedRiskDetail,
      combinedRiskLayout,
      combinedRiskLine,
      riskKeysFooter,
      combinedHierarchyDesign,
      combinedInfoLayout,
      combinedRiskWord,
      zonePanelFade,
      zoneStackedEmphasis,
      zoneColumnsEmphasis,
      zoneColumnsLevelList,
      ladderColumnClarityDesign,
      takeoverDetailFlushTop,
      riskHoverTitleSwatch,
      ladderRowHighlightDisabled,
      ladderHighlight,
      ladderDescriptionLayout,
    }),
    [hoveredPoint, hoveredTakeoverRiskArea, hoveredRiskAreaYear, hoveredRiskSpaceReadout, riskHoverDetail, riskHoverTedDefinition, riskHoverCapabilityLadder, riskTedPanelDesign, combinedRiskDetail, combinedRiskLayout, combinedRiskLine, riskKeysFooter, combinedHierarchyDesign, combinedInfoLayout, combinedRiskWord, zonePanelFade, zoneStackedEmphasis, zoneColumnsEmphasis, zoneColumnsLevelList, ladderColumnClarityDesign, takeoverDetailFlushTop, riskHoverTitleSwatch, ladderRowHighlightDisabled, ladderHighlight, ladderDescriptionLayout],
  )
  // Hover is not an input on this surface (mobile dashboard) — provide
  // null so every consumer renders its base, unhovered content.
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  return (
    <CapabilityHoverSwapContext.Provider value={hoverSwapsDisabled ? null : value}>
      {children}
    </CapabilityHoverSwapContext.Provider>
  )
}

// Mount as a child of <HudPersistentGlobeAndGraph> (inside its
// BandHudOverlayProvider): forwards the graph's point-on-line hover payload
// out to the CapabilityHoverSwap context, where the lower-half target can see
// it. Renders nothing.
export const CapabilityHoverBridge = () => {
  const swap = useCapabilityHoverSwap()
  const { pointTooltip, takeoverRiskAreaHover, riskAreaHoverYear, riskSpaceReadout } = useBandHudOverlay()
  const setHoveredPoint = swap?.setHoveredPoint
  const setHoveredTakeoverRiskArea = swap?.setHoveredTakeoverRiskArea
  const setHoveredRiskAreaYear = swap?.setHoveredRiskAreaYear
  const setHoveredRiskSpaceReadout = swap?.setHoveredRiskSpaceReadout
  useEffect(() => {
    if (!setHoveredPoint) return
    setHoveredPoint(pointTooltip)
    return () => setHoveredPoint(null)
  }, [pointTooltip, setHoveredPoint])
  useEffect(() => {
    if (!setHoveredTakeoverRiskArea) return
    setHoveredTakeoverRiskArea(takeoverRiskAreaHover)
    return () => setHoveredTakeoverRiskArea(false)
  }, [takeoverRiskAreaHover, setHoveredTakeoverRiskArea])
  useEffect(() => {
    if (!setHoveredRiskAreaYear) return
    setHoveredRiskAreaYear(riskAreaHoverYear)
    return () => setHoveredRiskAreaYear(null)
  }, [riskAreaHoverYear, setHoveredRiskAreaYear])
  useEffect(() => {
    if (!setHoveredRiskSpaceReadout) return
    setHoveredRiskSpaceReadout(riskSpaceReadout)
    return () => setHoveredRiskSpaceReadout(null)
  }, [riskSpaceReadout, setHoveredRiskSpaceReadout])
  return null
}

// A stat row: label on the left, the changing value bold and right-aligned,
// sharing one line.
const StatValueLine = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-3 font-mono">
    <span className="whitespace-nowrap text-[10px]">{label}</span>
    <span className="font-bold whitespace-nowrap text-[12px]">{value}</span>
  </div>
)

const TakeoverHeader = () => (
  <div>
    <HudHoverTitle>{CAPABILITY_HOVER_COPY.title}</HudHoverTitle>
    <HudHoverDescription>{CAPABILITY_HOVER_COPY.subtitle}</HudHoverDescription>
  </div>
)

// Layout knobs for the ladder rows below. The rows read as a timeline: a thin
// vertical rail down the left with one dot per level (the current level's dot
// filled in the accent color, the rest black rings with page-background
// centers), a fixed-width name column so every name starts on the same edge,
// and the one-liners in a second aligned column. Palette per
// design-style-guide.md: black & red only — all text full black, bold (not
// opacity) marks the current level.
const CAPABILITY_LADDER_STYLE = {
  // Dot + rail geometry is chosen to land on WHOLE device pixels at 2x
  // (verified 2026-07-07 by pixel-measuring headless-Chrome renders): a 7px
  // dot in the 12px column puts the dot's left edge at 2.5px (5 device px)
  // and its center at exactly 6px; the 1.5px ring is 3 device px, so the
  // ring draws the same thickness all the way around. The previous 6.5px /
  // 1.25px pair hit quarter-pixels and rendered visibly oval (13x11 device
  // px) with a lopsided ring.
  dotColumnWidthPx: 12,
  dotSizePx: 7,
  railWidthPx: 1,
  // 0 = the raw computed position: left (12 - 1) / 2 = 5.5px centers the
  // 1px rail at exactly 6px — the dots' center — to arbitrary precision.
  // (An eyeballed +0.1 nudge used to live here; it pushed the rail's paint
  // half a device pixel right of the dot centers.)
  railLeftNudgePx: 0,
  railColor: "#1a1a1a",
  // How far the rail's top/bottom ends stop short of the first/last row's
  // edges (past the first/last dot's center on the dotted treatments).
  railInsetPx: 8,
  currentDotColor: "var(--accent)",
  // Idle dots: black ring, page-background center.
  idleDotBorder: "1.5px solid #1a1a1a",
  idleDotFill: "#fffff8",
  nameColumnWidthPx: 145,
  currentRowBackground: "rgba(0, 0, 0, 0.05)",
  idleNameOpacity: 1,
  idleDescriptionOpacity: 1,
  currentDescriptionOpacity: 1,
}

// The no-highlight (highlightDisabled) treatment — tab 10: with the gray
// pill gone, the current level's NAME renders in red (all other text stays
// full black), and the whole ladder shifts left so the dots sit flush with
// the "AI Capabilities Line" title's left edge (the dots are centered in
// their 12px column, ~3px in).
const CAPABILITY_LADDER_NO_HIGHLIGHT_STYLE = {
  currentNameColor: "var(--accent)",
  leftShiftPx: -3,
}

// Red current-row treatments replacing the light-gray pill (one tab each),
// picked by CapabilityHoverSwap's ladderHighlight. Each borrows a proven
// family language so the ladder coheres with the power-breakdown emphasis:
//   "red-name"     — the current level's name in red (tab 10's treatment)
//   "rail-segment" — the rail turns into a thick red bar beside the row
//   "outline"      — a red hairline rounded outline around the row
//   "underline"    — a red rule under the current name (tab 14's language)
//   "chip"         — the name inverts into a solid red chip, cream text
//   "rail-glide"   — NO dots at all: a hairline black rail runs between the
//                    levels, and the segment beside the current row is a
//                    thick red bar (the datacenter list's 3px rail) that
//                    GLIDES between rows exactly like the stock gray pill;
//                    the current row's text goes red, its name bold
//   "rail-glide-even" — rail-glide, but the black line between the levels is
//                    the SAME thickness as the red bar (one even line whose
//                    current segment is red)
// The datacenter-hover-inspired family (2026-07-12) — all share rail-glide's
// BASE (no dots, hairline black line, bold current name) and vary the
// current-row mark along one axis each, borrowing the globe DC list's
// vocabulary (GLOBE_DC_ROW_HIGHLIGHT_STYLE / GLOBE_DC_RAIL_ANIM_VARIANTS):
//   "rail-glide-cut" — tab 1's exact geometry, but the red segment and red
//                    text arrive/leave with the DC hover's DEFAULT ANIMATION
//                    (45ms linear cut out, 120ms strong-ease-out settle in)
//                    instead of gliding
//   "row-invert"     — the whole current row fills solid red (gliding like
//                    the pill), its text cream — the DC list's "invert"
//   "row-underline"  — a red rule under the current row's full width glides
//                    between rows; text stays black — the DC "underline"
//   "row-frame"      — a red rounded outline glides around the current row;
//                    text stays black — the DC "frame"
//   "name-rail-cut"  — the production DC pick verbatim: a thick red bar hugs
//                    the current NAME's left edge, text red bold, cut/settle
//                    animation — the DC "rail-cut"
//   "red-text"       — tab 0's ladder (dots kept, current dot red) with NO
//                    highlight mark at all: the current row's name red +
//                    bold AND its one-liner red — the text alone carries it
//   "rail-glide-fill" — tab 1, but the red runs from the line's TOP all the
//                    way down to the current row's bottom — a progress fill
//                    instead of a one-segment mark
//   "rail-glide-thick" — tab 1 with the red bar 1px thicker (4px)
// Lighter-line takes on rail-glide-thick (2026-07-12) — same 4px red glide,
// the black line de-weighted three ways:
//   "rail-glide-thick-hairline" — the line at 0.5px (renders as a soft
//                    half-intensity line, symmetric about the red's center)
//   "rail-glide-thick-dotted" — the line 1px but DOTTED
//   "rail-glide-thick-dashed" — the line 1px but DASHED
//   "rail-glide-thick-dashed-black-body" — the dashed take, but the current
//                    row's one-liner stays BLACK (only the name reads red)
//   "rail-glide-thick-dashed-timeline" — the dashed take with the GRAPH's
//                    dash rhythm: 4px dash / 6px gap, matching the timeline's
//                    dashed future-capability line
//   "rail-glide-capability-line" — the black line matches the graph's dashed
//                    future-capability line exactly (2.5px stroke, 4/6 dash
//                    rhythm); the red segment stays the thicker 4px bar,
//                    echoing the graph's solid red past-line
//   "rail-glide-thick-right" — rail-glide-thick with the red bar OFF-CENTER:
//                    flush with the black line's LEFT edge (barely
//                    overlapping its 1px) and sticking out to the right
//   "rail-glide-thick-right-no-line" — the same off-center red bar with NO
//                    black line at all: the gliding red level-meter segment
//                    alone marks the ladder
//   "rail-glide-black-bold" — the no-line geometry with the red swapped for
//                    INK: a black gliding segment, and the current row's
//                    name AND one-liner bold black instead of red (tab 71 —
//                    does reserving red for the risk % improve hierarchy?)
export type CapabilityLadderHighlight =
  | "red-name"
  | "rail-segment"
  | "outline"
  | "underline"
  | "chip"
  | "rail-glide"
  | "rail-glide-even"
  | "rail-glide-cut"
  | "row-invert"
  | "row-underline"
  | "row-frame"
  | "name-rail-cut"
  | "red-text"
  | "rail-glide-fill"
  | "rail-glide-thick"
  | "rail-glide-thick-hairline"
  | "rail-glide-thick-dotted"
  | "rail-glide-thick-dashed"
  | "rail-glide-thick-dashed-black-body"
  | "rail-glide-thick-dashed-timeline"
  | "rail-glide-capability-line"
  | "rail-glide-thick-right"
  | "rail-glide-thick-right-no-line"
  | "rail-glide-black-bold"
export const CAPABILITY_LADDER_HIGHLIGHT_STYLE = {
  // "rail-glide-black-bold" — the segment in ink, the current row bold
  // black (name and description).
  railGlideBlackBold: { barColor: "#000000", boldCurrentDescription: true },
  redName: { nameColor: "var(--accent)" },
  // boldCurrentDescription: the current row's one-liner bolds along with its
  // name (tried on the rail-segment tab).
  railSegment: { widthPx: 3, color: "var(--accent)", boldCurrentDescription: true },
  outline: { borderWidthPx: 1.25, color: "var(--accent)", radiusPx: 2 },
  underline: { ruleWidthPx: 1.5, color: "var(--accent)", gapPx: 1, boldCurrentDescription: true },
  chip: {
    background: "var(--accent)",
    textColor: "#fffff8",
    // Negative margins cancel the padding so the name stays put whether or
    // not the chip is showing.
    padding: "0 4px",
    margin: "0 -4px",
    borderRadiusPx: 2,
  },
  railGlide: {
    // The red bar's thickness matches the datacenter hover list's rail
    // (GLOBE_DC_ROW_HIGHLIGHT_STYLE.rail.widthPx in
    // GlobeHoverDatacenterList.tsx).
    redWidthPx: 3,
    redColor: "var(--accent)",
    // The idle black line between the levels. 1px is the only width that
    // can sit EXACTLY centered inside the 3px red bar (both share center
    // x=6 in the dot column) with every edge on a whole device pixel at 2x
    // (black [5.5, 6.5], red [4.5, 7.5]) — a 0.5px hairline can only be
    // crisp OR centered, never both (verified by pixel-measuring a
    // headless-Chrome render, 2026-07-12).
    railWidthPx: 1,
    // Pure black (the shared railColor is #1a1a1a) — at this weight any
    // softness reads as gray.
    railColor: "#000000",
    railLeftNudgePx: 0,
    // How far the black line's ends stop short of the first/last row's
    // edges. 0 = the line spans exactly the red segment's travel range —
    // the red bar covers its row's FULL height, so on the top/bottom rows
    // the two lines end together (Joanna 2026-07-12; the stock ladder's
    // 8px railInsetPx was sized to stop at the level DOTS, which this
    // family doesn't have).
    railEndInsetPx: 0,
    // The whole current row's text (name + one-liner) reads in red; the name
    // is bold like every other treatment.
    currentTextColor: "var(--accent)",
  },
  // "rail-glide-even" defers to railGlide above for everything except the
  // idle black line's thickness, which matches the red bar's — one even line
  // whose current segment reads red.
  railGlideEven: {
    railWidthPx: 3,
  },
  // "rail-glide-cut" — tab 1's geometry; the in/out numbers are the DC
  // hover's DEFAULT ANIMATION (GLOBE_DC_RAIL_ANIM_VARIANTS["rail-cut"],
  // Joanna's 2026-07-09 pick): departure cuts, arrival settles.
  railGlideCut: {
    redWidthPx: 3,
    redColor: "var(--accent)",
    inMs: 120,
    inEasing: "cubic-bezier(0.16, 1, 0.3, 1)",
    outMs: 45,
    outEasing: "linear",
  },
  // "row-invert" — the DC list's invert language: solid red row, cream text.
  rowInvert: {
    background: "var(--accent)",
    textColor: "#fffff8",
    borderRadiusPx: 2,
  },
  // "row-underline" — the DC list's underline language (2px red rule under
  // the row's full width).
  rowUnderline: {
    heightPx: 2,
    bottomOffsetPx: 0,
    color: "var(--accent)",
  },
  // "row-frame" — the DC list's frame language (red rounded hairline box).
  rowFrame: {
    borderWidthPx: 1.5,
    borderRadiusPx: 3,
    color: "var(--accent)",
  },
  // "name-rail-cut" — the production DC row highlight verbatim: 3px red bar
  // a 5px gap left of the text, red bold text, cut/settle animation
  // (GLOBE_DC_ROW_HIGHLIGHT_STYLE.rail + GLOBE_DC_RAIL_ANIM_VARIANTS
  // ["rail-cut"] in GlobeHoverDatacenterList.tsx).
  nameRailCut: {
    widthPx: 3,
    gapPx: 5,
    color: "var(--accent)",
    inMs: 120,
    inEasing: "cubic-bezier(0.16, 1, 0.3, 1)",
    outMs: 45,
    outEasing: "linear",
  },
  // "red-text" — no mark at all on tab 0's dots-and-rail geometry: the
  // current row's name (bold) and one-liner both read in this color.
  redText: {
    currentTextColor: "var(--accent)",
  },
  // "rail-glide-fill" — the red runs from the line's top down to the
  // current row's bottom (each covered row's segment fades on the stock
  // 200ms clock as the current level moves).
  railGlideFill: {
    redWidthPx: 3,
    redColor: "var(--accent)",
  },
  // "rail-glide-thick" — tab 1's single gliding segment, 1px thicker. Red
  // [4, 8] and black [5.5, 6.5] still share center x=6 with every edge on a
  // whole device pixel at 2x.
  railGlideThick: {
    redWidthPx: 4,
    redColor: "var(--accent)",
  },
  // The lighter-line takes on rail-glide-thick — each defers to
  // railGlideThick for the red and (except the hairline) to railGlide for
  // the line color, and swaps only the line's weight/pattern.
  // The hairline is the exactly-centered RENDERING of a 0.5px line: 1px
  // wide at half intensity — the same ink an ideal 0.5px stroke straddling
  // the center would deposit. A literal 0.5px can't center on the 4px red
  // at 2x: Chrome snaps it to one device pixel, half a device pixel
  // off-center (pixel-measured 2026-07-12).
  railGlideThickHairline: {
    railWidthPx: 1,
    railColor: "rgba(0, 0, 0, 0.5)",
  },
  railGlideThickDotted: {
    railWidthPx: 1,
    railStyle: "dotted" as const,
  },
  railGlideThickDashed: {
    railWidthPx: 1,
    railStyle: "dashed" as const,
  },
  // "rail-glide-thick-dashed-timeline" — the dash rhythm copied from the
  // graph's dashed future-capability line (CAPABILITIES_FUTURE_LINE_DASHARRAY
  // = '4,6' in extrapolated-graph/graphConstants.ts): 4px of ink, 6px of
  // gap. CSS dashed borders can't take a custom pattern, so the line paints
  // as a repeating gradient with these stops.
  railGlideThickDashedTimeline: {
    railWidthPx: 1,
    dashPx: 4,
    gapPx: 6,
  },
  // "rail-glide-capability-line" — the black line matches the graph's
  // DASHED future-capability line exactly: its 2.5px stroke
  // (CAPABILITIES_FUTURE_LINE_STROKE_WIDTH) and its 4/6 dash rhythm
  // (CAPABILITIES_FUTURE_LINE_DASHARRAY, both in
  // extrapolated-graph/graphConstants.ts). The red bar stays the 4px
  // railGlideThick segment — thicker than the black, like the graph's solid
  // red past-line against its thinner dashed future.
  railGlideCapabilityLine: {
    railWidthPx: 2.5,
    dashPx: 4,
    gapPx: 6,
  },
}

// Description-cell layouts fixing the uneven / hover-shifting wraps (each
// gets a tab, paired with the rail-segment highlight). BOTH also reserve each
// row's height at BOLD metrics (a hidden bold copy sizes the cell), so
// bolding the current row can never move other rows:
//   "compact"  — SHORTENED copy (the dict below) at the normal size, so every
//                description is exactly one line, bold or not
//   "two-line" — the full copy, every description slot exactly two lines
//                tall, giving all rows one uniform height
export type CapabilityLadderDescriptionLayout = "compact" | "two-line"
export const CAPABILITY_LADDER_DESCRIPTION_LAYOUT_STYLE = {
  // rowPaddingYPx: vertical padding per row (the other layouts use 2.5) —
  // the one-line rows get extra breathing room between lines.
  compact: { fontSize: "9.5px", rowPaddingYPx: 5 },
  // Slot height in em (2 lines x the row line-height of 1.6).
  twoLine: { slotHeightEm: 3.2 },
}

// One-line copy for the "compact" layout, keyed by ladder label — same
// meaning as CAPABILITY_LEVEL_LADDER's descriptions, trimmed so each fits a
// single line at the normal row size even when bold. Levels not listed here
// fall back to the full description.
export const CAPABILITY_LEVEL_SHORT_DESCRIPTIONS: Record<string, string> = {
  "Unreliable Agent": "Handles multi-step tasks, but needs human checking.",
  "Reliable Agent": "Trusted with long tasks without constant checking.",
  "Automated coder": "Handles nearly all software engineering end to end.",
  "Superhuman AI Researcher": "Better at AI research than the best humans.",
  "Top-Expert-Dominating AI": "Outperforms top experts in nearly every field.",
  Superintelligent: "Far beyond human capability across the board.",
  "Wildly Superintelligent": "Beyond human science and comprehension.",
}

// How the highlight moves when the hovered level changes: the row-background
// pill GLIDES from the old row to the new one (motion layout animation), and
// the per-row emphasis (dot color, name weight/opacity, description opacity)
// transitions on the same clock — no snapping.
const CAPABILITY_LADDER_HIGHLIGHT_TRANSITION = { duration: 0.2, ease: "easeOut" as const }
const CAPABILITY_LADDER_ROW_TRANSITION_CSS =
  "background 200ms ease-out, border 200ms ease-out, color 200ms ease-out, opacity 200ms ease-out, font-weight 200ms ease-out"

// The custom-rhythm dashed lines (timeline-dash treatments) paint as a
// vertical repeating gradient: conf.dashPx of ink, conf.gapPx of gap.
const capabilityLadderDashGradientCss = (conf: { dashPx: number; gapPx: number }) =>
  `repeating-linear-gradient(to bottom, ${CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railColor} 0 ${conf.dashPx}px, transparent ${conf.dashPx}px ${conf.dashPx + conf.gapPx}px)`

// The cut-family treatments' asymmetric transition (the DC hover's DEFAULT
// ANIMATION shape): the state being ENTERED picks the timing, so becoming
// current settles in with `in*` and losing it cuts out with `out*`.
const capabilityLadderCutTransitionCss = (
  isCurrent: boolean,
  conf: { inMs: number; inEasing: string; outMs: number; outEasing: string },
) => {
  const timing = isCurrent ? `${conf.inMs}ms ${conf.inEasing}` : `${conf.outMs}ms ${conf.outEasing}`
  return `color ${timing}, opacity ${timing}, font-weight ${timing}`
}

// The full-width ladder rows — see CAPABILITY_LADDER_STYLE above for the
// treatment. `currentLabel` (matched against CAPABILITY_LEVEL_LADDER labels)
// picks the highlighted row; null renders all rows idle. Exported for the
// metric-details panel redesigns, which drive it from the live chart value
// rather than a graph hover.
export const CapabilityLadderRows = ({
  currentLabel,
  highlightDisabled = false,
  highlight,
  descriptionLayout,
}: {
  currentLabel: string | null
  // Drops the gray pill behind the current row (see CapabilityHoverSwap's
  // ladderRowHighlightDisabled) — red dot + bold name carry the emphasis.
  // Equivalent to highlight="red-name".
  highlightDisabled?: boolean
  // Red current-row treatment replacing the gray pill — see
  // CAPABILITY_LADDER_HIGHLIGHT_STYLE. Unset keeps the stock pill.
  highlight?: CapabilityLadderHighlight
  // Description-cell layout fixing uneven / hover-shifting wraps — see
  // CAPABILITY_LADDER_DESCRIPTION_LAYOUT_STYLE. Unset keeps natural wrapping.
  descriptionLayout?: CapabilityLadderDescriptionLayout
}) => {
  // Resolved treatment: the stock gray pill unless a red variant is picked.
  const treatment: CapabilityLadderHighlight | "pill" =
    highlight ?? (highlightDisabled ? "red-name" : "pill")
  // Tab 1's rail-glide BASE, shared by the whole DC-inspired family: no
  // level dots, hairline black ladder line (rail-glide-even alone swaps the
  // line's thickness). Each member layers its own current-row mark on top.
  // The thick-red subfamily: rail-glide-thick plus its lighter-line takes —
  // all share the 4px gliding red segment (railGlideThick's knobs).
  const thickRedFamily =
    treatment === "rail-glide-thick" ||
    treatment === "rail-glide-thick-hairline" ||
    treatment === "rail-glide-thick-dotted" ||
    treatment === "rail-glide-thick-dashed" ||
    treatment === "rail-glide-thick-dashed-black-body" ||
    treatment === "rail-glide-thick-dashed-timeline" ||
    treatment === "rail-glide-capability-line" ||
    treatment === "rail-glide-thick-right" ||
    treatment === "rail-glide-thick-right-no-line" ||
    treatment === "rail-glide-black-bold"
  // The off-center pair: red flush on the black line's left-edge POSITION
  // (the no-line take keeps the red exactly where it was, just unpainted).
  const thickRightFamily =
    treatment === "rail-glide-thick-right" ||
    treatment === "rail-glide-thick-right-no-line" ||
    treatment === "rail-glide-black-bold"
  const railGlideFamily =
    treatment === "rail-glide" ||
    treatment === "rail-glide-even" ||
    treatment === "rail-glide-cut" ||
    treatment === "row-invert" ||
    treatment === "row-underline" ||
    treatment === "row-frame" ||
    treatment === "name-rail-cut" ||
    treatment === "rail-glide-fill" ||
    thickRedFamily
  // Members whose current-row text reads RED (row-invert goes cream on its
  // red fill; row-underline / row-frame keep full black).
  const redCurrentText =
    (treatment === "rail-glide" ||
      treatment === "rail-glide-even" ||
      treatment === "rail-glide-cut" ||
      treatment === "name-rail-cut" ||
      treatment === "rail-glide-fill" ||
      thickRedFamily) &&
    // The black-bold take keeps every letter in ink.
    treatment !== "rail-glide-black-bold"
  // The black-body take keeps the red to the NAME; every other red-text
  // member reds the one-liner too.
  const redCurrentDescription =
    redCurrentText && treatment !== "rail-glide-thick-dashed-black-body"
  // Members animating with the DC cut/settle timing instead of the 200ms
  // symmetric row transition — and which dict entry carries their numbers.
  const cutTimingConf =
    treatment === "rail-glide-cut"
      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideCut
      : treatment === "name-rail-cut"
        ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.nameRailCut
        : null
  const ladderRailWidthPx = railGlideFamily
    ? treatment === "rail-glide-even"
      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideEven.railWidthPx
      : treatment === "rail-glide-thick-hairline"
        ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThickHairline.railWidthPx
        : treatment === "rail-glide-thick-dotted"
          ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThickDotted.railWidthPx
          : treatment === "rail-glide-thick-dashed" ||
              treatment === "rail-glide-thick-dashed-black-body"
            ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThickDashed.railWidthPx
            : treatment === "rail-glide-thick-dashed-timeline"
              ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThickDashedTimeline.railWidthPx
              : treatment === "rail-glide-capability-line"
                ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideCapabilityLine.railWidthPx
                : CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railWidthPx
    : CAPABILITY_LADDER_STYLE.railWidthPx
  // Dotted/dashed lines paint via border-left instead of a background fill;
  // the timeline-dash treatments paint as a repeating gradient (custom dash
  // rhythm — see capabilityLadderDashGradientCss).
  const ladderRailLineStyle: "solid" | "dotted" | "dashed" | "timeline-dash" =
    treatment === "rail-glide-thick-dotted"
      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThickDotted.railStyle
      : treatment === "rail-glide-thick-dashed" ||
          treatment === "rail-glide-thick-dashed-black-body"
        ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThickDashed.railStyle
        : treatment === "rail-glide-thick-dashed-timeline" ||
            treatment === "rail-glide-capability-line"
          ? "timeline-dash"
          : "solid"
  // The line block's widest element — the red bar for the treatments whose
  // red thickens the LINE, the black line itself for the ones marking the
  // row instead. The whole family left-aligns this element's left edge with
  // the takeover header's text (the ladder shifts left by the element's
  // offset inside the dot column).
  const lineMarkWidthPx =
    treatment === "rail-glide" || treatment === "rail-glide-even" || treatment === "rail-glide-cut"
      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.redWidthPx
      : treatment === "rail-glide-fill"
        ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideFill.redWidthPx
        : thickRedFamily
          ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThick.redWidthPx
          : ladderRailWidthPx
  // "-thick-right": the leftmost ink is the BLACK line's left edge (the red
  // sits flush on it and sticks out right), so the header-alignment shift
  // keys off the line, not the red's width.
  const railGlideFamilyLeftShiftPx =
    thickRightFamily
      ? -(CAPABILITY_LADDER_STYLE.dotColumnWidthPx - CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railWidthPx) / 2
      : -(CAPABILITY_LADDER_STYLE.dotColumnWidthPx - lineMarkWidthPx) / 2
  // "rail-glide-fill": every level at or above the current one gets its
  // line segment painted red — resolved once, by index.
  const currentIndex = CAPABILITY_LEVEL_LADDER.findIndex(
    (level) => level.label === currentLabel,
  )
  // Namespaces the highlight pill's layoutId, so a ladder in an open
  // metric-details panel and the hover takeover's ladder never animate
  // against each other.
  const layoutGroupId = useId()
  return (
    <LayoutGroup id={layoutGroupId}>
      <div
        className="relative"
        style={{
          // Both pill-less dots-and-rail treatments shift left so the dots
          // sit flush with the title's left edge; the rail-glide family
          // left-aligns its LINE with the header text instead.
          marginLeft: treatment === "red-name" || treatment === "red-text"
            ? CAPABILITY_LADDER_NO_HIGHLIGHT_STYLE.leftShiftPx
            : railGlideFamily
              ? railGlideFamilyLeftShiftPx
              : undefined,
        }}
      >
        {/* the rail connecting the level dots, inset past the first/last dot.
            left subtracts half the rail's own width so the rail's CENTER (not
            its left edge) sits on the dots' center line, plus the subpixel
            nudge. The rail-glide family thins it to its hairline width and
            paints it pure black (there are no dots to connect — the line IS
            the ladder); rail-glide-even alone thickens it to the red bar's
            width. "-thick-right-no-line" paints NO line at all — the red
            level-meter segment alone marks the ladder. */}
        {treatment !== "rail-glide-thick-right-no-line" && treatment !== "rail-glide-black-bold" && (
        <div
          className="absolute"
          style={{
            left:
              (CAPABILITY_LADDER_STYLE.dotColumnWidthPx - ladderRailWidthPx) / 2 +
              (railGlideFamily && treatment !== "rail-glide-even"
                ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railLeftNudgePx
                : CAPABILITY_LADDER_STYLE.railLeftNudgePx),
            // The rail-glide family's line runs the red segment's full
            // travel range (railEndInsetPx 0) — the stock dots ladder keeps
            // its dot-sized inset.
            top: railGlideFamily
              ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railEndInsetPx
              : CAPABILITY_LADDER_STYLE.railInsetPx,
            bottom: railGlideFamily
              ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railEndInsetPx
              : CAPABILITY_LADDER_STYLE.railInsetPx,
            // Dotted/dashed paint as a border-left on a zero-width element
            // (backgrounds can't dash); the timeline dash paints its custom
            // 4/6 rhythm as a repeating gradient; solid stays a plain fill.
            width:
              ladderRailLineStyle === "solid" || ladderRailLineStyle === "timeline-dash"
                ? ladderRailWidthPx
                : 0,
            background:
              ladderRailLineStyle === "timeline-dash"
                ? capabilityLadderDashGradientCss(
                    treatment === "rail-glide-capability-line"
                      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideCapabilityLine
                      : CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThickDashedTimeline,
                  )
                : ladderRailLineStyle === "solid"
                  ? treatment === "rail-glide-thick-hairline"
                    ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThickHairline.railColor
                    : railGlideFamily
                      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railColor
                      : CAPABILITY_LADDER_STYLE.railColor
                  : undefined,
            borderLeft:
              ladderRailLineStyle === "dotted" || ladderRailLineStyle === "dashed"
                ? `${ladderRailWidthPx}px ${ladderRailLineStyle} ${CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railColor}`
                : undefined,
          }}
        />
        )}
        {/* "rail-glide" (+ -even, the thick family): the red segment reads
            as a LEVEL METER — the black line divided into N equal parts
            (N = the ladder's level count), the red occupying exactly the
            current level's part, 1/Nth the line's height (Joanna
            2026-07-12; it used to span the current ROW's full, variable
            height). The line itself already runs the container's full
            height (railEndInsetPx 0), so the segments tile it exactly. One
            persistent element, so it still GLIDES between segments on the
            stock pill's 200ms clock. */}
        {(treatment === "rail-glide" ||
          treatment === "rail-glide-even" ||
          thickRedFamily) && (
          <motion.div
            className="pointer-events-none absolute"
            style={{
              // Centered on the black line — except "-thick-right", where
              // the red's LEFT edge sits flush on the line's left edge
              // (barely overlapping its 1px) and the rest sticks out right.
              left:
                thickRightFamily
                  ? (CAPABILITY_LADDER_STYLE.dotColumnWidthPx -
                      CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railWidthPx) / 2 +
                    CAPABILITY_LADDER_STYLE.railLeftNudgePx
                  : CAPABILITY_LADDER_STYLE.dotColumnWidthPx / 2 -
                    (thickRedFamily
                      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThick.redWidthPx
                      : CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.redWidthPx) / 2 +
                    CAPABILITY_LADDER_STYLE.railLeftNudgePx,
              width: thickRedFamily
                ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThick.redWidthPx
                : CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.redWidthPx,
              background:
                treatment === "rail-glide-black-bold"
                  ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideBlackBold.barColor
                  : thickRedFamily
                    ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideThick.redColor
                    : CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.redColor,
              height: `${100 / CAPABILITY_LEVEL_LADDER.length}%`,
            }}
            initial={false}
            animate={{
              top: `${(Math.max(currentIndex, 0) / CAPABILITY_LEVEL_LADDER.length) * 100}%`,
              opacity: currentIndex >= 0 ? 1 : 0,
            }}
            transition={CAPABILITY_LADDER_HIGHLIGHT_TRANSITION}
          />
        )}
        {CAPABILITY_LEVEL_LADDER.map((level, levelIndex) => {
          const isCurrent = level.label === currentLabel
          return (
            <div
              key={level.label}
              className="relative flex gap-2 py-[2.5px] pr-1"
              style={
                descriptionLayout === "compact"
                  ? {
                      paddingTop:
                        CAPABILITY_LADDER_DESCRIPTION_LAYOUT_STYLE.compact.rowPaddingYPx,
                      paddingBottom:
                        CAPABILITY_LADDER_DESCRIPTION_LAYOUT_STYLE.compact.rowPaddingYPx,
                    }
                  : undefined
              }
            >
              {/* the shared highlight pill — one instance, glides between rows */}
              {isCurrent && treatment === "pill" && (
                <motion.div
                  layoutId="capability-ladder-current-row"
                  className="absolute inset-0 rounded-[2px]"
                  style={{ background: CAPABILITY_LADDER_STYLE.currentRowBackground }}
                  transition={CAPABILITY_LADDER_HIGHLIGHT_TRANSITION}
                />
              )}
              {/* "outline": red hairline box around the current row */}
              {isCurrent && treatment === "outline" && (
                <span
                  className="pointer-events-none absolute inset-0"
                  style={{
                    border: `${CAPABILITY_LADDER_HIGHLIGHT_STYLE.outline.borderWidthPx}px solid ${CAPABILITY_LADDER_HIGHLIGHT_STYLE.outline.color}`,
                    borderRadius: CAPABILITY_LADDER_HIGHLIGHT_STYLE.outline.radiusPx,
                  }}
                />
              )}
              {/* the gliding red segment for rail-glide/-even/-thick lives at
                  the CONTAINER level now (see the level-meter element above
                  the rows) — it is one of N equal divisions of the black
                  line, not a row-height bar. */}
              {/* "rail-glide-fill": this row's line segment paints red when
                  the row is at or above the current level — collectively the
                  red runs from the line's top to the current row's bottom,
                  each segment fading on the stock 200ms clock. The first
                  row's segment starts where the rail starts (railInsetPx). */}
              {treatment === "rail-glide-fill" && (
                <span
                  className="pointer-events-none absolute"
                  style={{
                    left:
                      CAPABILITY_LADDER_STYLE.dotColumnWidthPx / 2 -
                      CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideFill.redWidthPx / 2 +
                      CAPABILITY_LADDER_STYLE.railLeftNudgePx,
                    // Starts where the (family) rail now starts — its full
                    // extent, not the stock dot inset.
                    top: levelIndex === 0
                      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.railEndInsetPx
                      : 0,
                    bottom: 0,
                    width: CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideFill.redWidthPx,
                    background: CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideFill.redColor,
                    opacity: currentIndex >= 0 && levelIndex <= currentIndex ? 1 : 0,
                    transition: CAPABILITY_LADDER_ROW_TRANSITION_CSS,
                  }}
                />
              )}
              {/* "rail-glide-cut": the same red segment, but each row owns
                  its own (always mounted, opacity-toggled) so the old one
                  can CUT out while the new one settles in — the DC hover's
                  default animation, no travel */}
              {treatment === "rail-glide-cut" && (
                <span
                  className="pointer-events-none absolute"
                  style={{
                    left:
                      CAPABILITY_LADDER_STYLE.dotColumnWidthPx / 2 -
                      CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideCut.redWidthPx / 2 +
                      CAPABILITY_LADDER_STYLE.railLeftNudgePx,
                    top: 0,
                    bottom: 0,
                    width: CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideCut.redWidthPx,
                    background: CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideCut.redColor,
                    opacity: isCurrent ? 1 : 0,
                    transition: capabilityLadderCutTransitionCss(
                      isCurrent,
                      CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideCut,
                    ),
                  }}
                />
              )}
              {/* "row-invert": the whole current row fills solid red (the DC
                  list's invert), gliding between rows like the pill */}
              {isCurrent && treatment === "row-invert" && (
                <motion.div
                  layoutId="capability-ladder-row-invert"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background: CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowInvert.background,
                    borderRadius: CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowInvert.borderRadiusPx,
                  }}
                  transition={CAPABILITY_LADDER_HIGHLIGHT_TRANSITION}
                />
              )}
              {/* "row-underline": a red rule under the current row's full
                  width (the DC list's underline), gliding between rows */}
              {isCurrent && treatment === "row-underline" && (
                <motion.div
                  layoutId="capability-ladder-row-underline"
                  className="pointer-events-none absolute left-0 right-0"
                  style={{
                    bottom: CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowUnderline.bottomOffsetPx,
                    height: CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowUnderline.heightPx,
                    background: CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowUnderline.color,
                  }}
                  transition={CAPABILITY_LADDER_HIGHLIGHT_TRANSITION}
                />
              )}
              {/* "row-frame": a red rounded outline around the current row
                  (the DC list's frame), gliding between rows */}
              {isCurrent && treatment === "row-frame" && (
                <motion.div
                  layoutId="capability-ladder-row-frame"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    border: `${CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowFrame.borderWidthPx}px solid ${CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowFrame.color}`,
                    borderRadius: CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowFrame.borderRadiusPx,
                  }}
                  transition={CAPABILITY_LADDER_HIGHLIGHT_TRANSITION}
                />
              )}
              {/* "rail-segment": the rail thickens red beside the current row */}
              {isCurrent && treatment === "rail-segment" && (
                <span
                  className="pointer-events-none absolute"
                  style={{
                    left:
                      CAPABILITY_LADDER_STYLE.dotColumnWidthPx / 2 -
                      CAPABILITY_LADDER_HIGHLIGHT_STYLE.railSegment.widthPx / 2,
                    top: 0,
                    bottom: 0,
                    width: CAPABILITY_LADDER_HIGHLIGHT_STYLE.railSegment.widthPx,
                    background: CAPABILITY_LADDER_HIGHLIGHT_STYLE.railSegment.color,
                  }}
                />
              )}
              <div
                className="relative shrink-0 flex justify-center"
                style={{ width: CAPABILITY_LADDER_STYLE.dotColumnWidthPx }}
              >
                {/* the rail-glide family has no level dots — the column
                    stays for the rail's geometry and the name column's
                    alignment */}
                {!railGlideFamily && (
                  <span
                    className="rounded-full"
                    style={{
                      width: CAPABILITY_LADDER_STYLE.dotSizePx,
                      height: CAPABILITY_LADDER_STYLE.dotSizePx,
                      marginTop: 4,
                      background: isCurrent
                        ? CAPABILITY_LADDER_STYLE.currentDotColor
                        : CAPABILITY_LADDER_STYLE.idleDotFill,
                      // 0-width border (not "none") when current, so the border
                      // TRANSITIONS between states instead of snapping.
                      border: isCurrent
                        ? "0px solid transparent"
                        : CAPABILITY_LADDER_STYLE.idleDotBorder,
                      transition: CAPABILITY_LADDER_ROW_TRANSITION_CSS,
                    }}
                  />
                )}
              </div>
              <div
                className="relative font-mono whitespace-nowrap shrink-0"
                style={{
                  width: CAPABILITY_LADDER_STYLE.nameColumnWidthPx,
                  fontSize: CAPABILITY_HOVER_STYLE.ladderRowFontSize,
                  lineHeight: CAPABILITY_HOVER_STYLE.ladderRowLineHeight,
                  fontWeight: isCurrent ? 700 : 400,
                  color:
                    isCurrent && treatment === "red-name"
                      ? CAPABILITY_LADDER_NO_HIGHLIGHT_STYLE.currentNameColor
                      : isCurrent && treatment === "red-text"
                        ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.redText.currentTextColor
                        : isCurrent && redCurrentText
                          ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.currentTextColor
                          : isCurrent && treatment === "row-invert"
                            ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowInvert.textColor
                            : undefined,
                  opacity: isCurrent ? 1 : CAPABILITY_LADDER_STYLE.idleNameOpacity,
                  transition: cutTimingConf
                    ? capabilityLadderCutTransitionCss(isCurrent, cutTimingConf)
                    : CAPABILITY_LADDER_ROW_TRANSITION_CSS,
                }}
              >
                {/* "name-rail-cut": the red bar hugging the current name's
                    left edge — always mounted so it cuts out / settles in
                    with the DC timing */}
                {treatment === "name-rail-cut" && (
                  <span
                    className="pointer-events-none absolute"
                    style={{
                      left: -(
                        CAPABILITY_LADDER_HIGHLIGHT_STYLE.nameRailCut.gapPx +
                        CAPABILITY_LADDER_HIGHLIGHT_STYLE.nameRailCut.widthPx
                      ),
                      top: 0,
                      bottom: 0,
                      width: CAPABILITY_LADDER_HIGHLIGHT_STYLE.nameRailCut.widthPx,
                      background: CAPABILITY_LADDER_HIGHLIGHT_STYLE.nameRailCut.color,
                      opacity: isCurrent ? 1 : 0,
                      transition: capabilityLadderCutTransitionCss(
                        isCurrent,
                        CAPABILITY_LADDER_HIGHLIGHT_STYLE.nameRailCut,
                      ),
                    }}
                  />
                )}
                {/* "chip": the name inverts into a red chip. "underline": a
                    red rule under the name — the transparent border is on
                    EVERY row so line heights never shift. */}
                {isCurrent && treatment === "chip" ? (
                  <span
                    style={{
                      background: CAPABILITY_LADDER_HIGHLIGHT_STYLE.chip.background,
                      color: CAPABILITY_LADDER_HIGHLIGHT_STYLE.chip.textColor,
                      padding: CAPABILITY_LADDER_HIGHLIGHT_STYLE.chip.padding,
                      margin: CAPABILITY_LADDER_HIGHLIGHT_STYLE.chip.margin,
                      borderRadius: CAPABILITY_LADDER_HIGHLIGHT_STYLE.chip.borderRadiusPx,
                    }}
                  >
                    {level.label}
                  </span>
                ) : treatment === "underline" ? (
                  <span
                    style={{
                      paddingBottom: CAPABILITY_LADDER_HIGHLIGHT_STYLE.underline.gapPx,
                      borderBottom: `${CAPABILITY_LADDER_HIGHLIGHT_STYLE.underline.ruleWidthPx}px solid ${
                        isCurrent
                          ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.underline.color
                          : "transparent"
                      }`,
                      transition: CAPABILITY_LADDER_ROW_TRANSITION_CSS,
                    }}
                  >
                    {level.label}
                  </span>
                ) : (
                  level.label
                )}
              </div>
              <div
                className="relative font-sans min-w-0"
                style={{
                  fontSize:
                    descriptionLayout === "compact"
                      ? CAPABILITY_LADDER_DESCRIPTION_LAYOUT_STYLE.compact.fontSize
                      : CAPABILITY_HOVER_STYLE.ladderRowFontSize,
                  lineHeight: CAPABILITY_HOVER_STYLE.ladderRowLineHeight,
                  fontWeight:
                    isCurrent &&
                    ((treatment === "rail-segment" &&
                      CAPABILITY_LADDER_HIGHLIGHT_STYLE.railSegment.boldCurrentDescription) ||
                      (treatment === "underline" &&
                        CAPABILITY_LADDER_HIGHLIGHT_STYLE.underline.boldCurrentDescription) ||
                      (treatment === "rail-glide-black-bold" &&
                        CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlideBlackBold.boldCurrentDescription))
                      ? 700
                      : 400,
                  // Red-text family members: the current row's one-liner
                  // reads in red along with its name (weight stays normal —
                  // bold is the name's alone); row-invert's goes cream on
                  // the red fill.
                  color:
                    isCurrent && treatment === "red-text"
                      ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.redText.currentTextColor
                      : isCurrent && redCurrentDescription
                        ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.railGlide.currentTextColor
                        : isCurrent && treatment === "row-invert"
                          ? CAPABILITY_LADDER_HIGHLIGHT_STYLE.rowInvert.textColor
                          : undefined,
                  opacity: isCurrent
                    ? CAPABILITY_LADDER_STYLE.currentDescriptionOpacity
                    : CAPABILITY_LADDER_STYLE.idleDescriptionOpacity,
                  minHeight:
                    descriptionLayout === "two-line"
                      ? `${CAPABILITY_LADDER_DESCRIPTION_LAYOUT_STYLE.twoLine.slotHeightEm}em`
                      : undefined,
                  transition: cutTimingConf
                    ? capabilityLadderCutTransitionCss(isCurrent, cutTimingConf)
                    : CAPABILITY_LADDER_ROW_TRANSITION_CSS,
                }}
              >
                {(() => {
                  // "compact" swaps in the one-line copy; other layouts keep
                  // the full description.
                  const description =
                    descriptionLayout === "compact"
                      ? CAPABILITY_LEVEL_SHORT_DESCRIPTIONS[level.label] ??
                        level.description
                      : level.description
                  return descriptionLayout ? (
                    // Grid-stacked hidden BOLD copy: the cell is always sized
                    // at bold metrics, so bolding the current row's
                    // description can never change any row's height.
                    <span className="grid">
                      <span
                        aria-hidden
                        className="invisible font-bold"
                        style={{ gridArea: "1 / 1" }}
                      >
                        {description}
                      </span>
                      <span style={{ gridArea: "1 / 1" }}>{description}</span>
                    </span>
                  ) : (
                    description
                  )
                })()}
              </div>
            </div>
          )
        })}
      </div>
    </LayoutGroup>
  )
}

// The "ladder" takeover (from HUD 107): the header row carries the speedup
// stat, then every level renders full-width with its one-liner beside it,
// the current level's row at full strength and the rest dimmed. Reads as a
// progression map. Other layouts (two-column, compact, speedup-first,
// ladder-defined, inline strip) live in the aesthetic-options archive.
// Exported for the unified-panels "ladder card" tab, which renders this same
// block inside the floating hover card instead of taking over the lower HUD.
export const CapabilityLadder = ({
  point,
  ladderRowHighlightDisabled = false,
  ladderHighlight,
  ladderDescriptionLayout,
}: {
  point: PointHoverTooltipPayload
  ladderRowHighlightDisabled?: boolean
  ladderHighlight?: CapabilityLadderHighlight
  ladderDescriptionLayout?: CapabilityLadderDescriptionLayout
}) => (
  <div>
    <div className="flex items-start justify-between gap-4">
      <TakeoverHeader />
      <div className="shrink-0 text-right">
        <StatValueLine
          label={CAPABILITY_HOVER_COPY.speedupLabel}
          value={point.speedupText}
        />
      </div>
    </div>
    <HudHoverDetailBody>
      <CapabilityLadderRows
        currentLabel={point.capabilityThresholdLabel}
        highlightDisabled={ladderRowHighlightDisabled}
        highlight={ladderHighlight}
        descriptionLayout={ladderDescriptionLayout}
      />
    </HudHoverDetailBody>
  </div>
)

// The combined hover's takeover (tab 14): the capability ladder's layout
// with BOTH live stats in the header's right column — the point's R&D
// speedup (the line hover's stat) and the takeover risk if capabilities
// foomed at the cursor's year (the risk-field hover's stat, in the field's
// ink). Copy in COMBINED_CAPABILITY_RISK_HOVER_COPY.
const CombinedCapabilityRiskDetail = ({
  point,
  ladderRowHighlightDisabled = false,
  ladderHighlight,
  ladderDescriptionLayout,
}: {
  point: PointHoverTooltipPayload
  ladderRowHighlightDisabled?: boolean
  ladderHighlight?: CapabilityLadderHighlight
  ladderDescriptionLayout?: CapabilityLadderDescriptionLayout
}) => {
  const swap = useCapabilityHoverSwap()
  const { targetDate } = useHudDates()
  const { activePlan } = useHudContext()
  const copy = COMBINED_CAPABILITY_RISK_HOVER_COPY
  const semantics = swap?.combinedRiskDetail ?? "foom"
  // The cursor's fractional year (published by the graph alongside the foom
  // preview), falling back to the story year in the frame between hovers.
  const yearStart = Date.UTC(targetDate.getUTCFullYear(), 0, 1)
  const nextYearStart = Date.UTC(targetDate.getUTCFullYear() + 1, 0, 1)
  const storyYear =
    targetDate.getUTCFullYear() +
    (targetDate.getTime() - yearStart) / (nextYearStart - yearStart)
  const riskYear = swap?.hoveredRiskAreaYear ?? storyYear
  // "foom": the risk-from-scaling table at the cursor's year.
  // "current-level": the table's TED-authored value at the cursor's year,
  // scaled to the level the line actually reaches then — see
  // currentLevelRiskAt. (An earlier take read the dither's density at the
  // line instead; this HUD's band is stylized to hug the line, so that
  // number was artificially low everywhere.)
  const risk =
    semantics === "current-level"
      ? point.capabilityValue === undefined
        ? null
        : currentLevelRiskAt(point.capabilityValue, activePlan, riskYear)
      : likelihoodAtYear(TAKEOVER_RISK_LIKELIHOOD_DATA, riskYear)
  const description =
    semantics === "current-level"
      ? COMBINED_CURRENT_LEVEL_RISK_COPY.description
      : copy.description
  const riskLabel =
    semantics === "current-level"
      ? COMBINED_CURRENT_LEVEL_RISK_COPY.riskLabel
      : copy.riskLabel
  // The stacked layout (tabs 22–24): the capability hover's own header
  // block, then the "AI Takeover Risk" section with the field hover's
  // simple definition and (per combinedRiskLine) one live percentage line.
  if (swap?.combinedRiskLayout === "two-sections") {
    const lineRisk =
      swap?.combinedRiskLine === "risk-level"
        ? point.capabilityValue === undefined
          ? null
          : currentLevelRiskAt(point.capabilityValue, activePlan, riskYear)
        : swap?.combinedRiskLine === "ted-this-year"
          ? likelihoodAtYear(TAKEOVER_RISK_LIKELIHOOD_DATA, riskYear)
          : null
    const lineLabel =
      swap?.combinedRiskLine === "risk-level"
        ? COMBINED_RISK_LINE_COPY.riskLevelLabel
        : COMBINED_RISK_LINE_COPY.tedThisYearLabel
    const hierarchy = swap?.combinedHierarchyDesign ?? "stock"
    const hierarchyStyle = COMBINED_HIERARCHY_STYLE
    const keysMode: RiskKeysFooterMode =
      hierarchy === "keys-footnote"
        ? "footnote"
        : hierarchy === "keys-run-in"
          ? "run-in"
          : hierarchy === "type-scale"
            ? "small-titles"
            : hierarchy === "title-icon-rule-run-in" || hierarchy === "rule-run-in"
              ? "run-in-colon"
              : hierarchy === "icon-rule" || hierarchy === "title-icon-rule"
                ? "small-swatches"
                : "stock"
    const showRule =
      hierarchy === "rule"
      || hierarchy === "icon-rule"
      || hierarchy === "title-icon-rule"
      || hierarchy === "title-icon-rule-run-in"
      || hierarchy === "rule-run-in"
    return (
      <div>
        <div className="flex items-start justify-between gap-4">
          {hierarchy === "icon" || hierarchy === "icon-rule" ? (
            // The capability-line glyph beside the title + subtitle — both
            // sections lead with a pictogram.
            <div className="flex items-start" style={{ gap: hierarchyStyle.iconGapPx }}>
              <CapabilityLineIcon />
              <TakeoverHeader />
            </div>
          ) : hierarchy === "title-icon-rule" || hierarchy === "title-icon-rule-run-in" ? (
            // The icon inline with the TITLE line only — small, title-height
            // — the subtitle runs full-width below.
            <div>
              <div className="flex items-center" style={{ gap: hierarchyStyle.titleIconGapPx }}>
                <CapabilityLineIconSmall />
                <HudHoverTitle>{CAPABILITY_HOVER_COPY.title}</HudHoverTitle>
              </div>
              <HudHoverDescription>{CAPABILITY_HOVER_COPY.subtitle}</HudHoverDescription>
            </div>
          ) : hierarchy === "type-scale" ? (
            // The capability title a size up (the key titles go a size
            // down) — hierarchy by type contrast.
            <div>
              <div
                className="font-mono font-bold uppercase tracking-wide"
                style={{ fontSize: hierarchyStyle.scaledTitleFontSizePx }}
              >
                {CAPABILITY_HOVER_COPY.title}
              </div>
              <div
                className="font-sans"
                style={{ fontSize: hierarchyStyle.scaledSubtitleFontSizePx, lineHeight: 1.4 }}
              >
                {CAPABILITY_HOVER_COPY.subtitle}
              </div>
            </div>
          ) : (
            <TakeoverHeader />
          )}
          <div className="shrink-0 text-right">
            <StatValueLine label={copy.speedupLabel} value={point.speedupText} />
          </div>
        </div>
        <HudHoverDetailBody>
          <CapabilityLadderRows
            currentLabel={point.capabilityThresholdLabel}
            highlightDisabled={ladderRowHighlightDisabled}
            highlight={ladderHighlight}
            descriptionLayout={ladderDescriptionLayout}
          />
        </HudHoverDetailBody>
        <div
          style={{
            marginTop: showRule
              ? hierarchyStyle.ruleGapAbovePx
              : COMBINED_TWO_SECTION_GAP_PX,
          }}
        >
          {/* The section separator IS the HUD's hover-separator rule (the
              same black dots that hang above the other hover details) —
              same component, same per-tab treatment. */}
          {showRule && (
            <HudHoverDetailRule
              style={{ marginBottom: hierarchyStyle.ruleGapBelowPx }}
            />
          )}
          {/* Tab 40: the two keys REPLACE the AI Takeover Risk header +
              subtitle — the columns carry the whole explanation. */}
          {swap?.riskKeysFooter ? (
            <RiskKeysFooter mode={keysMode} />
          ) : (
            <>
              <HudHoverTitle>{TAKEOVER_RISK_HOVER_COPY.title}</HudHoverTitle>
              <HudHoverDescription>{TAKEOVER_RISK_HOVER_COPY.description}</HudHoverDescription>
            </>
          )}
          {swap?.combinedRiskLine && lineRisk !== null && (
            <div
              className="font-mono"
              style={{
                marginTop: COMBINED_RISK_LINE_STYLE.marginTopPx,
                fontSize: COMBINED_RISK_LINE_STYLE.fontSize,
              }}
            >
              {lineLabel}
              <b
                style={{
                  marginLeft: COMBINED_RISK_LINE_STYLE.labelValueGapPx,
                  color: COMBINED_RISK_LINE_STYLE.valueColor,
                }}
              >
                {fmtLikelihood(lineRisk)}
              </b>
            </div>
          )}
        </div>
      </div>
    )
  }
  // Tab 19's info-layout variants (tabs 55–59) — see CombinedInfoLayoutId.
  const layout = swap?.combinedInfoLayout ?? "stock"
  const layoutStyle = COMBINED_INFO_LAYOUT_STYLE
  const riskValue = risk === null ? null : fmtLikelihood(risk)
  const ladder = (
    <HudHoverDetailBody>
      <CapabilityLadderRows
        currentLabel={point.capabilityThresholdLabel}
        highlightDisabled={ladderRowHighlightDisabled}
        highlight={ladderHighlight}
        descriptionLayout={ladderDescriptionLayout}
      />
    </HudHoverDetailBody>
  )
  const headerStats = (
    <div className="shrink-0 text-right">
      <StatValueLine label={copy.speedupLabel} value={point.speedupText} />
      {riskValue !== null && (
        <div className="flex items-baseline justify-between gap-3 font-mono">
          <span className="whitespace-nowrap text-[10px]">{riskLabel}</span>
          <span
            className="font-bold whitespace-nowrap text-[12px]"
            style={{ color: copy.riskValueColor }}
          >
            {riskValue}
          </span>
        </div>
      )}
    </div>
  )
  const descriptionNode =
    layout === "labeled-description" ? (
      // The dense combined sentence split into two labeled micro-sentences.
      <div
        className="font-sans"
        style={{
          fontSize: layoutStyle.labeledDescFontSizePx,
          lineHeight: layoutStyle.labeledDescLineHeight,
        }}
      >
        {[COMBINED_LABELED_DESCRIPTION.line, COMBINED_LABELED_DESCRIPTION.risk].map((row) => (
          <div key={row.label} className="flex">
            <b
              className="shrink-0 font-mono uppercase tracking-wider"
              style={{ width: layoutStyle.labeledDescLabelWidthPx, fontSize: layoutStyle.labeledDescFontSizePx - 2 }}
            >
              {row.label}
            </b>
            <span className="min-w-0">{row.text}</span>
          </div>
        ))}
      </div>
    ) : (
      <HudHoverDescription>{description}</HudHoverDescription>
    )
  if (layout === "stats-row") {
    // Full-width title + definition; the stats as their own row above the
    // ladder.
    return (
      <div>
        <HudHoverTitle>{copy.title}</HudHoverTitle>
        {descriptionNode}
        <div
          className="flex font-mono"
          style={{ marginTop: layoutStyle.statsRowMarginTopPx, gap: layoutStyle.statsRowGapPx }}
        >
          <div className="flex items-baseline gap-2">
            <span className="text-[10px]">{copy.speedupLabel}</span>
            <b className="text-[12px]">{point.speedupText}</b>
          </div>
          {riskValue !== null && (
            <div className="flex items-baseline gap-2">
              <span className="text-[10px]">{riskLabel}</span>
              <b className="text-[12px]" style={{ color: copy.riskValueColor }}>
                {riskValue}
              </b>
            </div>
          )}
        </div>
        {ladder}
      </div>
    )
  }
  if (layout === "risk-below") {
    // Each number lives with its section: speedup in the header, the risk
    // below the ladder as its own line.
    return (
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <HudHoverTitle>{copy.title}</HudHoverTitle>
            {descriptionNode}
          </div>
          <div className="shrink-0 text-right">
            <StatValueLine label={copy.speedupLabel} value={point.speedupText} />
          </div>
        </div>
        {ladder}
        {riskValue !== null && (
          <div
            className="font-mono"
            style={{ marginTop: layoutStyle.riskBelowMarginTopPx, fontSize: layoutStyle.riskBelowFontSizePx }}
          >
            {riskLabel}:
            <b className="ml-1.5" style={{ color: copy.riskValueColor }}>
              {riskValue}
            </b>
          </div>
        )}
      </div>
    )
  }
  if (layout === "level-cells-defs") {
    // Tab 74's short panel: tab 61's top — the title and the three cells
    // (level / speedup / risk) — with each cell's definition directly under
    // it, three equal columns. No ladder here: on tab 74 the agent table
    // lives on the labor hover, and this takeover only covers the
    // policies/power row.
    const columns = [
      {
        key: "level",
        valueClassName: "font-bold whitespace-nowrap",
        valueColor: undefined as string | undefined,
        valueText: point.capabilityThresholdLabel,
        label: layoutStyle.levelCellLabel,
        definition: LEVEL_CELLS_DEFS_COPY.level,
      },
      {
        key: "speedup",
        valueClassName: "font-bold",
        valueColor: undefined,
        valueText: point.speedupText,
        label: copy.speedupLabel,
        definition: LEVEL_CELLS_DEFS_COPY.speedup,
      },
      ...(riskValue !== null
        ? [
            {
              key: "risk",
              valueClassName: "font-bold",
              valueColor: copy.riskValueColor,
              valueText: riskValue,
              label: riskLabel,
              definition: LEVEL_CELLS_DEFS_COPY.risk,
            },
          ]
        : []),
    ]
    return (
      <div>
        <HudHoverTitle>{copy.title}</HudHoverTitle>
        <div
          className="flex"
          style={{ marginTop: layoutStyle.statsRowMarginTopPx, gap: layoutStyle.defColumnsGapPx }}
        >
          {columns.map((column) => (
            <div key={column.key} className="min-w-0 flex-1">
              <div className="font-mono">
                <div
                  className={column.valueClassName}
                  style={{
                    fontSize: layoutStyle.statCellValueFontSizePx,
                    color: column.valueColor,
                  }}
                >
                  {column.valueText}
                </div>
                <div style={{ fontSize: layoutStyle.statCellLabelFontSizePx }}>
                  {column.label}
                </div>
              </div>
              <div
                className="font-sans"
                style={{
                  marginTop: layoutStyle.defColumnsMarginTopPx,
                  fontSize: layoutStyle.defColumnsFontSizePx,
                  lineHeight: layoutStyle.defColumnsLineHeight,
                }}
              >
                {column.definition}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }
  const cellsFamily =
    layout === "stat-cells"
    || layout === "level-cells"
    || layout === "level-cells-fixed"
    || layout === "level-cells-tail"
    || layout === "cells-icons"
    || layout === "cells-def-columns"
    || layout === "cells-def-columns-icons"
    || layout === "cells-def-columns-three"
    || layout === "cells-def-columns-ladder-in-column"
  if (cellsFamily) {
    // The stats as HUD-style cells (bold value, small label under) in a row
    // under the title — see CombinedInfoLayoutId for the family's variants.
    const hasLevelCell =
      layout === "level-cells" || layout === "level-cells-fixed" || layout === "level-cells-tail"
    // "level-cells-fixed": reserved widths so the row never shifts as the
    // level name changes; "level-cells-tail" plants the numbers first and
    // lets the level breathe at the row's end.
    const levelCell = hasLevelCell && (
      <div
        key="level"
        style={layout === "level-cells-fixed" ? { width: layoutStyle.levelCellFixedWidthPx } : undefined}
      >
        <div className="font-bold whitespace-nowrap" style={{ fontSize: layoutStyle.statCellValueFontSizePx }}>
          {point.capabilityThresholdLabel}
        </div>
        <div style={{ fontSize: layoutStyle.statCellLabelFontSizePx }}>
          {layoutStyle.levelCellLabel}
        </div>
      </div>
    )
    const speedupCell = (
      <div
        key="speedup"
        className={layout === "cells-icons" ? "flex items-center" : undefined}
        style={{
          ...(layout === "level-cells-fixed" ? { width: layoutStyle.speedupCellFixedWidthPx } : {}),
          ...(layout === "cells-icons" ? { gap: layoutStyle.cellIconGapPx } : {}),
        }}
      >
        {layout === "cells-icons" && <CapabilityLineIconSmall />}
        <div>
          <div className="font-bold" style={{ fontSize: layoutStyle.statCellValueFontSizePx }}>
            {point.speedupText}
          </div>
          <div style={{ fontSize: layoutStyle.statCellLabelFontSizePx }}>{copy.speedupLabel}</div>
        </div>
      </div>
    )
    const riskCell = riskValue !== null && (
      <div
        key="risk"
        className={layout === "cells-icons" ? "flex items-center" : undefined}
        style={layout === "cells-icons" ? { gap: layoutStyle.cellIconGapPx } : undefined}
      >
        {layout === "cells-icons" && (
          <DitherKeySwatch
            color={RISK_KEYS_FOOTER_STYLE.swatchRedColor}
            rowRadii={COMBINED_HIERARCHY_STYLE.footnoteSwatchRowRadiiRed}
          />
        )}
        <div>
          <div
            className="font-bold"
            style={{ fontSize: layoutStyle.statCellValueFontSizePx, color: copy.riskValueColor }}
          >
            {riskValue}
          </div>
          <div style={{ fontSize: layoutStyle.statCellLabelFontSizePx }}>{riskLabel}</div>
        </div>
      </div>
    )
    if (layout === "cells-def-columns-three") {
      // All THREE things in a row — agent level, R&D speedup, takeover risk
      // — one column each, its definition beneath. The level column's
      // definition is the hovered level's own ladder one-liner (dynamic).
      const levelDescription =
        CAPABILITY_LEVEL_LADDER.find((level) => level.label === point.capabilityThresholdLabel)
          ?.description ?? ""
      const columns = [
        {
          key: "level",
          value: point.capabilityThresholdLabel,
          valueColor: undefined as string | undefined,
          label: layoutStyle.levelCellLabel,
          definition: levelDescription,
        },
        {
          key: "speedup",
          value: point.speedupText,
          valueColor: undefined as string | undefined,
          label: copy.speedupLabel,
          definition: CELLS_DEF_COLUMNS_COPY.speedupThree,
        },
        ...(riskValue !== null
          ? [{
              key: "risk",
              value: riskValue,
              valueColor: copy.riskValueColor as string | undefined,
              label: riskLabel,
              definition: CELLS_DEF_COLUMNS_COPY.risk,
            }]
          : []),
      ]
      return (
        <div>
          <HudHoverTitle>{copy.title}</HudHoverTitle>
          <div
            className="flex"
            style={{ marginTop: layoutStyle.statsRowMarginTopPx, gap: layoutStyle.defColumnsGapPx }}
          >
            {columns.map((column) => (
              <div key={column.key} className="min-w-0 flex-1">
                <div className="font-mono">
                  <div
                    className="font-bold"
                    style={{ fontSize: layoutStyle.statCellValueFontSizePx, color: column.valueColor }}
                  >
                    {column.value}
                  </div>
                  <div style={{ fontSize: layoutStyle.statCellLabelFontSizePx }}>
                    {column.label}
                  </div>
                </div>
                <div
                  className="font-sans"
                  style={{
                    marginTop: layoutStyle.defColumnsMarginTopPx,
                    fontSize: layoutStyle.defColumnsFontSizePx,
                    lineHeight: layoutStyle.defColumnsLineHeight,
                  }}
                >
                  {column.definition}
                </div>
              </div>
            ))}
          </div>
          {ladder}
        </div>
      )
    }
    if (
      layout === "cells-def-columns-ladder-in-column"
      && swap?.ladderColumnClarityDesign
    ) {
      // Tab 76's clarity variants — see LADDER_COLUMN_CLARITY_DESIGNS.
      const clarity = LADDER_COLUMN_CLARITY_DESIGNS[swap.ladderColumnClarityDesign]
      const clarityStyle = LADDER_COLUMN_CLARITY_STYLE
      // `red` inks the header text (tab 89's riskHeaderRed — the Takeover
      // Risk side's header in the field's red).
      const columnHeader = (icon: React.ReactNode, text: string, red = false) =>
        clarity.header ? (
          clarity.headerTier === "title" ? (
            // The title's spot goes to the individual names — one
            // title-tier header per column.
            <div
              className="flex items-center"
              style={{
                gap: clarityStyle.headerIconGapPx,
                marginBottom: clarityStyle.headerMarginBottomPx,
                color: red ? clarityStyle.redHeaderColor : undefined,
              }}
            >
              {clarity.iconPlacement === "header" && icon}
              <HudHoverTitle>{text}</HudHoverTitle>
            </div>
          ) : (
            <div
              className="flex items-center font-mono font-bold uppercase tracking-wider"
              style={{
                gap: clarityStyle.headerIconGapPx,
                marginBottom: clarityStyle.headerMarginBottomPx,
                fontSize: clarityStyle.headerFontSizePx,
                color: red ? clarityStyle.redHeaderColor : undefined,
              }}
            >
              {clarity.iconPlacement === "header" && icon}
              {text}
            </div>
          )
        ) : null
      // The word-scale display (riskCombinedRiskWord) applies here too.
      const clarityRiskDisplay =
        swap?.combinedRiskWord && risk !== null ? riskWordForFraction(risk) : riskValue
      const smallLabel = (text: string) => (
        <div className="font-mono" style={{ fontSize: layoutStyle.statCellLabelFontSizePx }}>
          {text}
        </div>
      )
      const definition = (text: string) =>
        clarity.defs ? (
          <div
            className="font-sans"
            style={{
              marginTop: clarityStyle.defMarginTopPx,
              fontSize: clarity.defFontSizePx ?? layoutStyle.defColumnsFontSizePx,
              lineHeight: layoutStyle.defColumnsLineHeight,
            }}
          >
            {text}
          </div>
        ) : null
      // The capability column's two data blocks — order flips with
      // clarity.levelFirst (the level as the leading human fact).
      const speedupBlock = (
        <>
          <div
            className="flex items-center font-mono"
            style={{
              gap: layoutStyle.cellIconGapPx,
              marginTop: clarity.levelFirst
                ? SINGLE_LEVEL_READOUT_STYLE.spacedMarginTopPx
                : undefined,
            }}
          >
            {clarity.iconPlacement === "value" && <CapabilityLineIconSmall />}
            <span className="font-bold" style={{ fontSize: clarityStyle.valueFontSizePx }}>
              {point.speedupText}
            </span>
          </div>
          {(clarity.valueLabels === "both" || clarity.valueLabels === "left") &&
            smallLabel(copy.speedupLabel)}
        </>
      )
      return (
        <div className="relative">
          {clarity.yearBadge && (
            // Pins every live value to the cursor's moment.
            <span
              className="absolute right-0 top-0 font-mono"
              style={{ fontSize: CLARITY_YEAR_BADGE.fontSizePx, color: CLARITY_YEAR_BADGE.color }}
            >
              {CLARITY_YEAR_BADGE.prefix}
              <b>{Math.floor(riskYear)}</b>
            </span>
          )}
          {clarity.title && <HudHoverTitle>{copy.title}</HudHoverTitle>}
          <div
            className="flex"
            style={{
              marginTop: clarity.title ? layoutStyle.statsRowMarginTopPx : 0,
              gap: layoutStyle.defColumnsGapPx,
            }}
          >
            <div className="min-w-0 flex-1">
              {columnHeader(
                <CapabilityLineIconSmall
                  size={clarity.headerTier === "title" ? clarityStyle.titleHeaderGlyphSizePx : undefined}
                />,
                LADDER_COLUMN_CLARITY_COPY.capabilityHeader,
              )}
              {!clarity.levelFirst && speedupBlock}
              {clarity.capabilityDef !== false && definition(CELLS_DEF_COLUMNS_COPY.capability)}
              {clarity.levelDisplay === "single" ? (
                // The agent level as ONE thing: the current level's name
                // with its own one-liner beneath (no list of the others).
                <div
                  style={{
                    marginTop: clarity.levelFirst
                      ? 0
                      : clarity.levelSpaced
                        ? SINGLE_LEVEL_READOUT_STYLE.spacedMarginTopPx
                        : SINGLE_LEVEL_READOUT_STYLE.marginTopPx,
                  }}
                >
                  <div
                    className="flex items-center font-mono font-bold whitespace-nowrap"
                    style={{
                      gap: layoutStyle.cellIconGapPx,
                      fontSize: SINGLE_LEVEL_READOUT_STYLE.nameFontSizePx,
                      color: clarity.levelNameBlack
                        ? SINGLE_LEVEL_READOUT_STYLE.nameInkColor
                        : SINGLE_LEVEL_READOUT_STYLE.nameColor,
                    }}
                  >
                    {clarity.wordSwapAnimation ? (
                      <AnimatedWordSwap text={point.capabilityThresholdLabel} />
                    ) : (
                      point.capabilityThresholdLabel
                    )}
                    {/* the seven-level strip rides to the RIGHT of the name
                        (Joanna 2026-07-15; was before it) */}
                    {clarity.levelDots && (
                      <LevelDotsRow
                        currentLabel={point.capabilityThresholdLabel}
                        marginTopPx={0}
                      />
                    )}
                  </div>
                  <div
                    className="font-sans"
                    style={{
                      marginTop: SINGLE_LEVEL_READOUT_STYLE.descriptionMarginTopPx,
                      fontSize: SINGLE_LEVEL_READOUT_STYLE.descriptionFontSizePx,
                      lineHeight: SINGLE_LEVEL_READOUT_STYLE.descriptionLineHeight,
                      minHeight: `${SINGLE_LEVEL_READOUT_STYLE.descriptionSlotEm}em`,
                    }}
                  >
                    {clarity.wordSwapAnimation ? (
                      <AnimatedWordSwap
                        block
                        text={
                          CAPABILITY_LEVEL_LADDER.find(
                            (level) => level.label === point.capabilityThresholdLabel,
                          )?.description ?? ""
                        }
                      />
                    ) : (
                      CAPABILITY_LEVEL_LADDER.find(
                        (level) => level.label === point.capabilityThresholdLabel,
                      )?.description ?? ""
                    )}
                  </div>
                </div>
              ) : (
                <CompactLevelList currentLabel={point.capabilityThresholdLabel} />
              )}
              {clarity.levelFirst && speedupBlock}
            </div>
            <div className="min-w-0 flex-1">
              {columnHeader(
                <DitherKeySwatch
                  color={RISK_KEYS_FOOTER_STYLE.swatchRedColor}
                  rowRadii={
                    clarity.headerTier === "title"
                      ? RISK_KEYS_FOOTER_STYLE.swatchRowRadiiRed
                      : COMBINED_HIERARCHY_STYLE.footnoteSwatchRowRadiiRed
                  }
                />,
                LADDER_COLUMN_CLARITY_COPY.riskHeader,
                clarity.riskHeaderRed,
              )}
              {clarityRiskDisplay !== null && (
                <div
                  className="flex items-center font-mono"
                  style={{ gap: layoutStyle.cellIconGapPx }}
                >
                  {clarity.iconPlacement === "value" && (
                    <DitherKeySwatch
                      color={RISK_KEYS_FOOTER_STYLE.swatchRedColor}
                      rowRadii={COMBINED_HIERARCHY_STYLE.footnoteSwatchRowRadiiRed}
                    />
                  )}
                  <span
                    className="font-bold whitespace-nowrap"
                    style={{ fontSize: clarityStyle.valueFontSizePx, color: copy.riskValueColor }}
                  >
                    {clarity.wordSwapAnimation ? (
                      <AnimatedWordSwap text={clarityRiskDisplay} />
                    ) : (
                      clarityRiskDisplay
                    )}
                  </span>
                  {clarity.riskPercent && risk !== null && (
                    // The word's numeric ruler.
                    <span className="font-mono whitespace-nowrap text-[9px]">
                      · {fmtLikelihood(risk)}
                    </span>
                  )}
                  {clarity.riskDotsScale && <RiskWordDotsRow fraction={risk} />}
                </div>
              )}
              {clarity.valueLabels === "both" &&
                smallLabel(
                  clarity.riskLabelShort ? LADDER_COLUMN_CLARITY_COPY.riskLabelShort : riskLabel,
                )}
              {definition(CELLS_DEF_COLUMNS_COPY.risk)}
            </div>
          </div>
        </div>
      )
    }
    if (
      layout === "cells-def-columns"
      || layout === "cells-def-columns-icons"
      || layout === "cells-def-columns-ladder-in-column"
    ) {
      // Two paired columns: each metric's cell with its definition directly
      // beneath it (Joanna's 2026-07-14 clarification — the data lives in
      // the columns too, not in its own row). The "-icons" take drops the
      // small titles and puts an icon beside each big digit instead.
      const iconCells =
        layout === "cells-def-columns-icons" || layout === "cells-def-columns-ladder-in-column"
      const speedupTop = iconCells ? (
        <div className="flex items-center" style={{ gap: layoutStyle.cellIconGapPx }}>
          <CapabilityLineIconSmall />
          <span className="font-bold" style={{ fontSize: layoutStyle.statCellValueFontSizePx }}>
            {point.speedupText}
          </span>
        </div>
      ) : (
        speedupCell
      )
      // Tab 72: the risk displays as the word scale (RISK_WORD_SCALE); the
      // percentage stays the underlying number.
      const riskDisplay =
        swap?.combinedRiskWord && risk !== null ? riskWordForFraction(risk) : riskValue
      const riskTop = iconCells ? (
        riskDisplay !== null && (
          <div className="flex items-center" style={{ gap: layoutStyle.cellIconGapPx }}>
            <DitherKeySwatch
              color={RISK_KEYS_FOOTER_STYLE.swatchRedColor}
              rowRadii={COMBINED_HIERARCHY_STYLE.footnoteSwatchRowRadiiRed}
            />
            <span
              className="font-bold whitespace-nowrap"
              style={{ fontSize: layoutStyle.statCellValueFontSizePx, color: copy.riskValueColor }}
            >
              {riskDisplay}
            </span>
          </div>
        )
      ) : (
        riskCell
      )
      return (
        <div>
          <HudHoverTitle>{copy.title}</HudHoverTitle>
          <div
            className="flex"
            style={{ marginTop: layoutStyle.statsRowMarginTopPx, gap: layoutStyle.defColumnsGapPx }}
          >
            <div className="min-w-0 flex-1">
              <div className="font-mono">{speedupTop}</div>
              {layout === "cells-def-columns-ladder-in-column" && (
                // With the level list below, the bare digit read ambiguous —
                // the small label names it (Joanna 2026-07-15).
                <div
                  className="font-mono"
                  style={{ fontSize: layoutStyle.statCellLabelFontSizePx }}
                >
                  {copy.speedupLabel}
                </div>
              )}
              <div
                className="font-sans"
                style={{
                  marginTop: layoutStyle.defColumnsMarginTopPx,
                  fontSize: layoutStyle.defColumnsFontSizePx,
                  lineHeight: layoutStyle.defColumnsLineHeight,
                }}
              >
                {CELLS_DEF_COLUMNS_COPY.capability}
              </div>
              {layout === "cells-def-columns-ladder-in-column" && (
                // The agent levels live in this column — names only, with a
                // single reserved slot for the active one-liner.
                <CompactLevelList currentLabel={point.capabilityThresholdLabel} />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono">{riskTop}</div>
              <div
                className="font-sans"
                style={{
                  marginTop: layoutStyle.defColumnsMarginTopPx,
                  fontSize: layoutStyle.defColumnsFontSizePx,
                  lineHeight: layoutStyle.defColumnsLineHeight,
                }}
              >
                {CELLS_DEF_COLUMNS_COPY.risk}
              </div>
            </div>
          </div>
          {layout !== "cells-def-columns-ladder-in-column" && ladder}
        </div>
      )
    }
    const cells =
      layout === "level-cells-tail"
        ? [speedupCell, riskCell, levelCell]
        : [levelCell, speedupCell, riskCell]
    return (
      <div>
        <HudHoverTitle>
          {layout === "cells-icons" ? COMBINED_CELLS_ICONS_TITLE : copy.title}
        </HudHoverTitle>
        <div
          className="flex font-mono"
          style={{ marginTop: layoutStyle.statsRowMarginTopPx, gap: layoutStyle.statsRowGapPx }}
        >
          {cells}
        </div>
        {hasLevelCell ? (
          // The definition sits below the cells, a step down the hierarchy.
          <div
            className="font-sans"
            style={{
              marginTop: layoutStyle.cellsDescriptionMarginTopPx,
              fontSize: layoutStyle.cellsDescriptionFontSizePx,
              lineHeight: 1.45,
            }}
          >
            {description}
          </div>
        ) : (
          descriptionNode
        )}
        {ladder}
      </div>
    )
  }
  if (layout === "description-last") {
    // The prose demotes to fine print below the ladder.
    return (
      <div>
        <div className="flex items-start justify-between gap-4">
          <HudHoverTitle>{copy.title}</HudHoverTitle>
          {headerStats}
        </div>
        {ladder}
        <div
          className="font-sans"
          style={{
            marginTop: layoutStyle.descriptionLastMarginTopPx,
            fontSize: layoutStyle.descriptionLastFontSizePx,
            lineHeight: 1.45,
          }}
        >
          {description}
        </div>
      </div>
    )
  }
  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <HudHoverTitle>{copy.title}</HudHoverTitle>
          {descriptionNode}
        </div>
        {headerStats}
      </div>
      {ladder}
    </div>
  )
}

// ─── The risk-area hover's optional visuals ─────────────────────────────────
// Which visual the takeover appends below the title + subtitle (see
// CapabilityHoverSwap's riskHoverDetail): a risk-level ladder or the
// yearly dot-density likelihood chart. Any of them also brings the live
// "85% in 2027" phrase on the header line, driven by the cursor's year over
// the field. Unset keeps the copy-only stock takeover.
// The ladder family (row content per RISK_HOVER_LADDER_VARIANTS):
//   "ladder"                      — the 5-band ladder, names + ranges +
//                                   descriptions (tabs 2/4/6–10)
//   "ladder-extended"             — the 7-band ladder (75%+ / 50%+ / 40–50%
//                                   splits above the old 40%+ top), full rows
//   "ladder-extended-ranges"      — 7 bands, NO names: bold range leads each
//                                   row, description beside
//   "ladder-extended-ranges-only" — 7 bands, ranges alone — a compact meter
// "stat-only" (tab 18) skips the visual AND the "85% in 2027" phrase: the
// risk instead rides the header's right side in the R&D-speedup stat
// grammar (TAKEOVER_RISK_HOVER_STAT) — one number, no over-time chart.
// "space-ladder" (tab 20) pairs with the 2D risk-space explorer
// (riskSpaceExplorer): the panel shows the CAPABILITY ladder with the
// current row tracking the level at the cursor's HEIGHT (gliding as you
// move vertically), the phrase carrying that level's risk-in-that-year —
// copy in RISK_SPACE_HOVER_DESCRIPTION.
// "ted-line" (tabs 26–30, from tab 24's bottom section): title + the simple
// definition + one live line — "Risk if Top-Expert-Dominating AI level was
// achieved this year: 83%" (COMBINED_RISK_LINE_COPY.tedThisYearLabel; the
// "risk-line" TED definition swaps in the parenthetical-definition label).
export type RiskHoverDetailVisual =
  | "ladder"
  | "ladder-extended"
  | "ladder-extended-ranges"
  | "ladder-extended-ranges-only"
  | "dot-chart"
  | "stat-only"
  | "space-ladder"
  | "ted-line"

// The space explorer panel's subtitle — the level/risk pair itself is live
// (the phrase and the ladder's gliding highlight). DRAFT copy — edit freely.
export const RISK_SPACE_HOVER_DESCRIPTION =
  "If AI reached the capability level under your cursor in that year — how likely it is to disempower humanity, given that year's projected alignment techniques."

// The "stat-only" header stat — the speedup stat's grammar, the value in
// the field's ink.
export const TAKEOVER_RISK_HOVER_STAT = {
  label: "Takeover Risk if foomed",
  valueColor: "var(--accent)",
}

// What each ladder visual renders per row. `levels` picks the band set
// (takeoverRiskLikelihoodData.ts).
export const RISK_HOVER_LADDER_VARIANTS: Record<
  Exclude<RiskHoverDetailVisual, "dot-chart" | "stat-only" | "space-ladder" | "ted-line">,
  { levels: RiskLevelLadderEntry[]; showNames: boolean; showDescriptions: boolean }
> = {
  ladder: {
    levels: TAKEOVER_RISK_LEVEL_LADDER,
    showNames: true,
    showDescriptions: true,
  },
  "ladder-extended": {
    levels: TAKEOVER_RISK_LEVEL_LADDER_EXTENDED,
    showNames: true,
    showDescriptions: true,
  },
  "ladder-extended-ranges": {
    levels: TAKEOVER_RISK_LEVEL_LADDER_EXTENDED,
    showNames: false,
    showDescriptions: true,
  },
  "ladder-extended-ranges-only": {
    levels: TAKEOVER_RISK_LEVEL_LADDER_EXTENDED,
    showNames: false,
    showDescriptions: false,
  },
}

export const RISK_HOVER_DETAIL_STYLE = {
  // The header phrase ("85% in 2027") — the details headers' grammar at the
  // hover scale: bold value in the field's ink, regular "in", bold year.
  nowPhraseFontSize: "12px",
  nowPhraseWordGapPx: 5,
  nowPhraseValueColor: "var(--accent)",
  visualMarginTopPx: 12,
}

// The risk-level ladder rows — the production capability ladder's
// "rail-glide-thick-right-no-line" grammar (see
// CAPABILITY_LADDER_HIGHLIGHT_STYLE): no line, no dots, a thick red
// level-meter segment gliding beside the current regime, whose row's text
// reads red with a bold name. Geometry literals mirror that treatment's
// resolved values (4px red bar at x=5.5 in the 12px column, container
// shifted -5.5 so the bar's left edge sits flush with the header text).
export const RISK_LEVEL_LADDER_STYLE = {
  barWidthPx: 4,
  barColor: "var(--accent)",
  barColumnWidthPx: 12,
  barLeftPx: 5.5,
  leftShiftPx: -5.5,
  nameColumnWidthPx: 110,
  // The no-names variants lead each row with the bold range instead — its
  // fixed column keeps the descriptions aligned.
  rangeColumnWidthPx: 52,
  currentTextColor: "var(--accent)",
  rowPaddingYPx: 2.5,
}

// `currentValue` (the takeover-risk likelihood 0..1 at the cursor's year)
// picks the highlighted regime; null renders all rows idle. `levels` picks
// the band set; showNames/showDescriptions trim the row content (see
// RISK_HOVER_LADDER_VARIANTS).
export const RiskLevelLadderRows = ({
  currentValue,
  levels = TAKEOVER_RISK_LEVEL_LADDER,
  showNames = true,
  showDescriptions = true,
}: {
  currentValue: number | null
  levels?: RiskLevelLadderEntry[]
  showNames?: boolean
  showDescriptions?: boolean
}) => {
  const style = RISK_LEVEL_LADDER_STYLE
  const currentIndex =
    currentValue === null
      ? -1
      : levels.findIndex((level) => level.matches(currentValue))
  return (
    <div className="relative" style={{ marginLeft: style.leftShiftPx }}>
      {/* the gliding red level-meter segment — one persistent element, one
          of N equal divisions of the ladder's height, on the capability
          ladder's 200ms clock */}
      <motion.div
        className="pointer-events-none absolute"
        style={{
          left: style.barLeftPx,
          width: style.barWidthPx,
          background: style.barColor,
          height: `${100 / levels.length}%`,
        }}
        initial={false}
        animate={{
          top: `${(Math.max(currentIndex, 0) / levels.length) * 100}%`,
          opacity: currentIndex >= 0 ? 1 : 0,
        }}
        transition={CAPABILITY_LADDER_HIGHLIGHT_TRANSITION}
      />
      {levels.map((level, levelIndex) => {
        const isCurrent = levelIndex === currentIndex
        const rowTextStyle = {
          fontSize: `var(--hud-hover-body-size, ${CAPABILITY_HOVER_STYLE.ladderRowFontSize})`,
          lineHeight: CAPABILITY_HOVER_STYLE.ladderRowLineHeight,
          color: isCurrent ? style.currentTextColor : undefined,
          transition: CAPABILITY_LADDER_ROW_TRANSITION_CSS,
        }
        return (
          <div
            key={level.range}
            className="relative flex gap-2 pr-1"
            style={{
              paddingTop: style.rowPaddingYPx,
              paddingBottom: style.rowPaddingYPx,
            }}
          >
            <div className="shrink-0" style={{ width: style.barColumnWidthPx }} />
            {showNames && (
              <div
                className="relative shrink-0 whitespace-nowrap font-mono"
                style={{
                  ...rowTextStyle,
                  width: style.nameColumnWidthPx,
                  fontWeight: isCurrent ? 700 : 400,
                }}
              >
                {level.label}
              </div>
            )}
            {showNames ? (
              <div className="relative min-w-0 flex-1 font-sans" style={rowTextStyle}>
                <b className="font-mono" style={{ fontWeight: isCurrent ? 700 : 400 }}>
                  {level.range}
                </b>
                {showDescriptions && <>{" — "}{level.description}</>}
              </div>
            ) : (
              <>
                {/* No names: the bold range leads the row in its own fixed
                    column, so the descriptions stay edge-aligned. */}
                <div
                  className="relative shrink-0 whitespace-nowrap font-mono"
                  style={{
                    ...rowTextStyle,
                    width: showDescriptions ? style.rangeColumnWidthPx : undefined,
                    fontWeight: isCurrent ? 700 : 400,
                  }}
                >
                  {level.range}
                </div>
                {showDescriptions && (
                  <div className="relative min-w-0 flex-1 font-sans" style={rowTextStyle}>
                    {level.description}
                  </div>
                )}
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

// The dot-density likelihood chart: one bar per year, drawn as a COLUMN OF
// DITHER DOTS — the main graph's field language, so the chart and the field
// it explains read as one instrument. Years up to the current story year dot
// at full size; projection years dot smaller (de-emphasis by size, never
// opacity) — the full→small boundary IS the now marker. Dots sit on one
// shared grid anchored at the plot's baseline, so rows align across bars
// like the field's grid.
export const RISK_DOT_CHART_STYLE = {
  widthPx: 455,
  heightPx: 96,
  padTopPx: 12,
  padBottomPx: 15,
  padLeftPx: 30,
  padRightPx: 4,
  barWidthFraction: 0.7,
  ditherCellPx: 4.5,
  pastDotRadiusPx: 1.5,
  futureDotRadiusPx: 0.85,
  // Near-zero years still register as a visible stub on the baseline.
  zeroBarStubPx: 1.5,
  labelFontSize: "8.5px",
  axisColor: "#111111",
  // Year tick spacing (integer years) once the domain outgrows yearly labels.
  tickEveryYears: 4,
  color: "var(--accent)",
  // The pinned readout above the cursor-year's bar ("2027 · 85%").
  readoutFontSize: "9px",
  readoutHaloColor: "#fffff8",
}

export const RiskLikelihoodMiniGraph = ({
  points,
  currentYear,
  // The field cursor's year pins the readout label to that year's bar.
  readoutYear = null,
}: {
  points: RiskAreaLikelihoodPoint[]
  currentYear: number
  readoutYear?: number | null
}) => {
  const patternId = useId()
  if (points.length < 2) return null
  const style = RISK_DOT_CHART_STYLE
  const minYear = points[0].year
  const maxYear = points[points.length - 1].year
  if (maxYear <= minYear) return null
  const plotWidth = style.widthPx - style.padLeftPx - style.padRightPx
  const plotHeight = style.heightPx - style.padTopPx - style.padBottomPx
  const baselineY = style.padTopPx + plotHeight
  // One slot per (yearly) point; bars center in their slots.
  const slotWidth = plotWidth / points.length
  const barWidth = slotWidth * style.barWidthFraction
  const slotCenterX = (index: number) => style.padLeftPx + (index + 0.5) * slotWidth
  const clamp01 = (value: number) => Math.min(1, Math.max(0, value))
  const yFor = (value: number) => style.padTopPx + (1 - clamp01(value)) * plotHeight
  // Every year when the domain is short; sparser ticks once the window
  // widens past what yearly labels can fit.
  const tickStep =
    Math.floor(maxYear) - Math.ceil(minYear) <= 8 ? 1 : style.tickEveryYears
  // FLOORED, so the pinned bar always agrees with the header phrase's
  // "in <year>" (nearest-bar rounding could pin one year ahead mid-year).
  const flooredReadoutYear = readoutYear === null ? null : Math.floor(readoutYear)
  const readoutIndexRaw =
    flooredReadoutYear === null
      ? -1
      : points.findIndex((point) => Math.floor(point.year) === flooredReadoutYear)
  const readoutIndex = readoutIndexRaw === -1 ? null : readoutIndexRaw
  const readout = readoutIndex !== null ? points[readoutIndex] : null
  return (
    <svg
      width={style.widthPx}
      height={style.heightPx}
      className="block"
      style={{ overflow: "visible" }}
      aria-hidden
    >
      <defs>
        {/* One shared dot grid, phase-anchored at the plot's left edge and
            BASELINE (userSpaceOnUse), so dot rows/columns line up across
            bars exactly like the field's grid. Two sizes of the same dot:
            full for realized years, small for the projection. */}
        {([
          ["past", style.pastDotRadiusPx],
          ["future", style.futureDotRadiusPx],
        ] as const).map(([kind, dotRadius]) => (
          <pattern
            key={kind}
            id={`${patternId}-${kind}`}
            width={style.ditherCellPx}
            height={style.ditherCellPx}
            patternUnits="userSpaceOnUse"
            x={style.padLeftPx}
            y={baselineY}
          >
            <circle
              cx={style.ditherCellPx / 2}
              cy={style.ditherCellPx / 2}
              r={dotRadius}
              fill={style.color}
            />
          </pattern>
        ))}
      </defs>
      {points.map((point, index) => {
        const realized = point.year <= currentYear
        const top = yFor(point.value)
        const barHeight = baselineY - top
        // Near-zero years register as a solid stub on the baseline (a dot
        // column this short would show nothing at all).
        if (barHeight < style.ditherCellPx) {
          return (
            <rect
              key={point.year}
              x={slotCenterX(index) - barWidth / 2}
              y={baselineY - style.zeroBarStubPx}
              width={barWidth}
              height={style.zeroBarStubPx}
              fill={style.color}
            />
          )
        }
        return (
          <rect
            key={point.year}
            x={slotCenterX(index) - barWidth / 2}
            y={top}
            width={barWidth}
            height={barHeight}
            fill={`url(#${patternId}-${realized ? "past" : "future"})`}
          />
        )
      })}
      {/* year ticks — no axis line (the numbers carry the axis) */}
      {points.map((point, index) =>
        Number.isInteger(point.year) && (point.year - Math.ceil(minYear)) % tickStep === 0 ? (
          <text
            key={`tick-${point.year}`}
            x={slotCenterX(index)}
            y={style.heightPx - 3}
            textAnchor="middle"
            className="font-mono"
            fontSize={style.labelFontSize}
            fill={style.axisColor}
          >
            {point.year}
          </text>
        ) : null,
      )}
      {/* likelihood scale: just the two ends, out of the plot's way */}
      <text
        x={style.padLeftPx - 5}
        y={yFor(1) + 3}
        textAnchor="end"
        className="font-mono"
        fontSize={style.labelFontSize}
        fill={style.axisColor}
      >
        100%
      </text>
      <text
        x={style.padLeftPx - 5}
        y={baselineY}
        textAnchor="end"
        className="font-mono"
        fontSize={style.labelFontSize}
        fill={style.axisColor}
      >
        0%
      </text>
      {/* The cursor-year's readout, paper-haloed above its bar. */}
      {readout && readoutIndex !== null && (
        <text
          x={slotCenterX(readoutIndex)}
          y={yFor(readout.value) - 5}
          textAnchor="middle"
          className="font-mono"
          fontSize={style.readoutFontSize}
          fontWeight="bold"
          fill={style.color}
          stroke={style.readoutHaloColor}
          strokeWidth={3}
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          {`${Math.floor(readout.year)} · ${fmtLikelihood(readout.value)}`}
        </text>
      )}
    </svg>
  )
}

// The takeover shown while the takeover-risk FIELD above the line is hovered:
// the ladder takeover's header voice, carrying TAKEOVER_RISK_HOVER_COPY.
// With a riskHoverDetail visual set, the header line also carries the LIVE
// likelihood as a "85% in 2027" phrase (the cursor's x over the field drives
// the year, like the point hover drives the capability ladder), and the
// visual renders under the subtitle.
const TakeoverRiskAreaDetail = () => {
  const swap = useCapabilityHoverSwap()
  const { targetDate } = useHudDates()
  const visual = swap?.riskHoverDetail
  const hoveredYear = swap?.hoveredRiskAreaYear ?? null
  // The story's (fractional) year — the chart's realized/projection boundary
  // and the phrase's fallback in the frame between hover states.
  const yearStart = Date.UTC(targetDate.getUTCFullYear(), 0, 1)
  const nextYearStart = Date.UTC(targetDate.getUTCFullYear() + 1, 0, 1)
  const storyYear =
    targetDate.getUTCFullYear() +
    (targetDate.getTime() - yearStart) / (nextYearStart - yearStart)
  const currentYear = hoveredYear ?? storyYear
  const spaceReadout = swap?.hoveredRiskSpaceReadout ?? null
  // The phrase's value: the space explorer reads the cursor-cell's authored
  // risk (level at the cursor's HEIGHT, that year); every other visual
  // reads the TED table at the cursor's year.
  const nowValue =
    visual === "space-ladder"
      ? spaceReadout?.riskFraction ?? null
      : visual
        ? likelihoodAtYear(TAKEOVER_RISK_LIKELIHOOD_DATA, currentYear)
        : null
  const style = RISK_HOVER_DETAIL_STYLE
  // The skimmability designs own the whole ted-line layout; "stock" keeps
  // the shared path below (tabs 26–33 unchanged).
  if (visual === "ted-line" && (swap?.riskTedPanelDesign ?? "stock") !== "stock") {
    return <TedLinePanel nowValue={nowValue} currentYear={currentYear} />
  }
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        {/* Tab 34: the field's red dither swatch inline with its name
            (riskHoverTitleSwatch) — same icon as the keys/cells swatches. */}
        {swap?.riskHoverTitleSwatch ? (
          <div
            className="flex items-center"
            style={{ gap: RISK_HOVER_TITLE_SWATCH_GAP_PX }}
          >
            <DitherKeySwatch
              color={RISK_KEYS_FOOTER_STYLE.swatchRedColor}
              rowRadii={COMBINED_HIERARCHY_STYLE.footnoteSwatchRowRadiiRed}
            />
            <HudHoverTitle>{TAKEOVER_RISK_HOVER_COPY.title}</HudHoverTitle>
          </div>
        ) : (
          <HudHoverTitle>{TAKEOVER_RISK_HOVER_COPY.title}</HudHoverTitle>
        )}
        {visual && visual !== "stat-only" && visual !== "ted-line" && nowValue !== null && (
          <span
            className="flex shrink-0 items-baseline whitespace-nowrap font-mono"
            style={{ fontSize: style.nowPhraseFontSize }}
          >
            <b style={{ color: style.nowPhraseValueColor }}>{fmtLikelihood(nowValue)}</b>
            {/* The space explorer drops the "in 2029" (Joanna 2026-07-15) —
                its timeline guide already shows the year in red at the
                axis; the other visuals keep the full phrase. */}
            {visual !== "space-ladder" && (
              <>
                <span style={{ marginLeft: style.nowPhraseWordGapPx, marginRight: style.nowPhraseWordGapPx }}>
                  in
                </span>
                <b>{Math.floor(currentYear)}</b>
              </>
            )}
          </span>
        )}
        {visual === "stat-only" && nowValue !== null && (
          // The speedup stat's grammar (StatValueLine), the value in the
          // field's ink — "show the risk the way you show the speedup".
          <div className="flex shrink-0 items-baseline justify-between gap-3 font-mono">
            <span className="whitespace-nowrap text-[10px]">
              {TAKEOVER_RISK_HOVER_STAT.label}
            </span>
            <span
              className="font-bold whitespace-nowrap text-[12px]"
              style={{ color: TAKEOVER_RISK_HOVER_STAT.valueColor }}
            >
              {fmtLikelihood(nowValue)}
            </span>
          </div>
        )}
      </div>
      <HudHoverDescription>
        {visual === "space-ladder"
          ? RISK_SPACE_HOVER_DESCRIPTION
          : swap?.riskHoverTedDefinition === "inline"
            ? TAKEOVER_RISK_HOVER_DESCRIPTION_TED_INLINE
            : TAKEOVER_RISK_HOVER_COPY.description}
      </HudHoverDescription>
      {swap?.riskHoverTedDefinition === "footnote" && (
        <div
          className="font-sans"
          style={{
            marginTop: TAKEOVER_RISK_TED_FOOTNOTE.marginTopPx,
            fontSize: TAKEOVER_RISK_TED_FOOTNOTE.textFontSizePx,
            lineHeight: TAKEOVER_RISK_TED_FOOTNOTE.lineHeight,
          }}
        >
          <b
            className="mr-1.5 font-mono font-bold uppercase tracking-wider"
            style={{ fontSize: TAKEOVER_RISK_TED_FOOTNOTE.labelFontSizePx }}
          >
            {TAKEOVER_RISK_TED_FOOTNOTE.label}
          </b>
          {TAKEOVER_RISK_TED_FOOTNOTE.text}
        </div>
      )}
      {visual === "ted-line" && nowValue !== null && (
        // Tab 24's bottom line as the field hover's whole payload: one live
        // "Risk if TED was achieved this year: 83%" line (the "risk-line"
        // TED definition swaps in the parenthetical-definition label).
        <div
          className="font-mono"
          style={{
            marginTop: COMBINED_RISK_LINE_STYLE.marginTopPx,
            fontSize: COMBINED_RISK_LINE_STYLE.fontSize,
          }}
        >
          {swap?.riskHoverTedDefinition === "risk-line"
            ? TED_RISK_LINE_LABEL_WITH_DEFINITION
            : COMBINED_RISK_LINE_COPY.tedThisYearLabel}
          <b
            style={{
              marginLeft: COMBINED_RISK_LINE_STYLE.labelValueGapPx,
              color: COMBINED_RISK_LINE_STYLE.valueColor,
            }}
          >
            {fmtLikelihood(nowValue)}
          </b>
        </div>
      )}
      {visual === "ted-line" && swap?.riskHoverCapabilityLadder && (
        // Tab 32: every level defined, the Top-Expert-Dominating row pinned
        // as the highlighted one (the risk line above is ABOUT that level,
        // so the mark stays put while the cursor moves).
        <div style={{ marginTop: style.visualMarginTopPx }}>
          <CapabilityLadderRows
            currentLabel={CURRENT_LEVEL_RISK_TED_LABEL}
            highlightDisabled={swap?.ladderRowHighlightDisabled}
            highlight={swap?.ladderHighlight}
            descriptionLayout={swap?.ladderDescriptionLayout}
          />
        </div>
      )}
      {visual && visual !== "dot-chart" && visual !== "stat-only" && visual !== "space-ladder" && visual !== "ted-line" && (
        <div style={{ marginTop: style.visualMarginTopPx }}>
          <RiskLevelLadderRows
            currentValue={nowValue}
            levels={RISK_HOVER_LADDER_VARIANTS[visual].levels}
            showNames={RISK_HOVER_LADDER_VARIANTS[visual].showNames}
            showDescriptions={RISK_HOVER_LADDER_VARIANTS[visual].showDescriptions}
          />
        </div>
      )}
      {visual === "space-ladder" && (
        // The CAPABILITY ladder, its current row tracking the level at the
        // cursor's height — glides through the rungs as you sweep the field
        // vertically.
        <div style={{ marginTop: style.visualMarginTopPx }}>
          <CapabilityLadderRows
            currentLabel={spaceReadout?.levelLabel ?? null}
            highlightDisabled={swap?.ladderRowHighlightDisabled}
            highlight={swap?.ladderHighlight}
            descriptionLayout={swap?.ladderDescriptionLayout}
          />
        </div>
      )}
      {visual === "dot-chart" && (
        <div style={{ marginTop: style.visualMarginTopPx }}>
          <RiskLikelihoodMiniGraph
            points={TAKEOVER_RISK_YEARLY_POINTS}
            currentYear={storyYear}
            readoutYear={hoveredYear}
          />
        </div>
      )}
    </div>
  )
}

// ─── Tab 69: the STABLE two-section panel (zoneStackedEmphasis) ──────────────
// Hovering EITHER zone shows this same stacked panel — the capabilities
// section (title, subtitle, live speedup stat, ladder) over the AI Takeover
// Risk section (title, definition, live TED risk line) — and the hovered
// zone's section takes the red emphasis: a leading marker square plus the
// title's ink turning red. Crossing the line/field boundary GLIDES the
// emphasis between the sections (emphasisTransitionCss); no content swaps,
// nothing moves.
export const ZONE_EMPHASIS_PANEL_STYLE = {
  activeTitleColor: "var(--accent)",
  idleTitleColor: "#000",
  markerSizePx: 6,
  markerGapPx: 7,
  emphasisTransitionCss: "color 200ms ease-out, opacity 200ms ease-out",
  // The capability stat's value while the FIELD is hovered (no point on the
  // line to read) — keeps the slot occupied so nothing shifts.
  speedupPlaceholder: "—",
}

// A section title with the gliding zone emphasis: a red marker square (space
// always reserved — it fades, never appears) plus the title ink turning red
// while `active`.
const ZoneEmphasisSectionTitle = ({
  active,
  children,
}: {
  active: boolean
  children: ReactNode
}) => {
  const style = ZONE_EMPHASIS_PANEL_STYLE
  return (
    <div className="flex items-center" style={{ gap: style.markerGapPx }}>
      <span
        className="shrink-0"
        style={{
          width: style.markerSizePx,
          height: style.markerSizePx,
          backgroundColor: style.activeTitleColor,
          opacity: active ? 1 : 0,
          transition: style.emphasisTransitionCss,
        }}
      />
      <div
        style={{
          color: active ? style.activeTitleColor : style.idleTitleColor,
          transition: style.emphasisTransitionCss,
        }}
      >
        <HudHoverTitle>{children}</HudHoverTitle>
      </div>
    </div>
  )
}

const ZoneEmphasisStackedDetail = ({
  point,
}: {
  point: PointHoverTooltipPayload | null
}) => {
  const swap = useCapabilityHoverSwap()
  const { targetDate } = useHudDates()
  const style = ZONE_EMPHASIS_PANEL_STYLE
  const lineActive = point !== null
  const yearStart = Date.UTC(targetDate.getUTCFullYear(), 0, 1)
  const nextYearStart = Date.UTC(targetDate.getUTCFullYear() + 1, 0, 1)
  const storyYear =
    targetDate.getUTCFullYear() +
    (targetDate.getTime() - yearStart) / (nextYearStart - yearStart)
  // The live risk year follows the cursor in EITHER zone — the hovered
  // point's year on the line, the published field year above it — with the
  // story year in the transient frames between hover states.
  const riskYear = point?.year ?? swap?.hoveredRiskAreaYear ?? storyYear
  const risk = likelihoodAtYear(TAKEOVER_RISK_LIKELIHOOD_DATA, riskYear)
  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <ZoneEmphasisSectionTitle active={lineActive}>
            {CAPABILITY_HOVER_COPY.title}
          </ZoneEmphasisSectionTitle>
          <HudHoverDescription>{CAPABILITY_HOVER_COPY.subtitle}</HudHoverDescription>
        </div>
        <div className="shrink-0 text-right">
          <StatValueLine
            label={CAPABILITY_HOVER_COPY.speedupLabel}
            value={point?.speedupText ?? style.speedupPlaceholder}
          />
        </div>
      </div>
      <HudHoverDetailBody>
        <CapabilityLadderRows
          currentLabel={point?.capabilityThresholdLabel ?? null}
          highlightDisabled={swap?.ladderRowHighlightDisabled}
          highlight={swap?.ladderHighlight}
          descriptionLayout={swap?.ladderDescriptionLayout}
        />
      </HudHoverDetailBody>
      <div style={{ marginTop: COMBINED_TWO_SECTION_GAP_PX }}>
        <ZoneEmphasisSectionTitle active={!lineActive}>
          {TAKEOVER_RISK_HOVER_COPY.title}
        </ZoneEmphasisSectionTitle>
        <HudHoverDescription>{TAKEOVER_RISK_HOVER_COPY.description}</HudHoverDescription>
        {risk !== null && (
          <div
            className="font-mono"
            style={{
              marginTop: COMBINED_RISK_LINE_STYLE.marginTopPx,
              fontSize: COMBINED_RISK_LINE_STYLE.fontSize,
            }}
          >
            {COMBINED_RISK_LINE_COPY.tedThisYearLabel}
            <b
              style={{
                marginLeft: COMBINED_RISK_LINE_STYLE.labelValueGapPx,
                color: COMBINED_RISK_LINE_STYLE.valueColor,
              }}
            >
              {fmtLikelihood(risk)}
            </b>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Tab 75: the TWO-COLUMN focus panel (zoneColumnsEmphasis) ────────────────
// Tab 69's stable-panel idea in tab 65's two-column grammar: hovering EITHER
// zone shows the same two metric columns — AI Capabilities (line icon + live
// speedup + definition) and AI Takeover Risk (red dither swatch + live % +
// definition) — and the hovered zone's column takes the focus: a red bar
// GLIDES along the top edge from column to column as the pointer crosses the
// line/field boundary, and the column's small name re-inks red. Nothing
// swaps, nothing moves; the focus travels.
export const ZONE_COLUMNS_PANEL_STYLE = {
  columnGapPx: 24,
  valueFontSizePx: 15,
  iconGapPx: 6,
  nameFontSizePx: 8.5,
  nameMarginTopPx: 3,
  activeNameColor: "var(--accent)",
  idleNameColor: "#000",
  definitionFontSizePx: 10,
  definitionLineHeight: 1.45,
  definitionMarginTopPx: 6,
  focusBarHeightPx: 2,
  focusBarColor: "var(--accent)",
  // Lane the bar rides in above the columns — reserved, so nothing shifts.
  focusBarGapBelowPx: 8,
  nameEmphasisTransitionCss: "color 200ms ease-out",
  focusBarTransitionCss: "transform 200ms cubic-bezier(0.16, 1, 0.3, 1)",
  // The capability value while the FIELD is hovered (no point on the line
  // to read) — keeps the slot occupied so nothing shifts.
  speedupPlaceholder: "—",
}

const ZoneEmphasisColumnsDetail = ({
  point,
}: {
  point: PointHoverTooltipPayload | null
}) => {
  const swap = useCapabilityHoverSwap()
  const { targetDate } = useHudDates()
  const { activePlan } = useHudContext()
  const style = ZONE_COLUMNS_PANEL_STYLE
  const lineActive = point !== null
  const yearStart = Date.UTC(targetDate.getUTCFullYear(), 0, 1)
  const nextYearStart = Date.UTC(targetDate.getUTCFullYear() + 1, 0, 1)
  const storyYear =
    targetDate.getUTCFullYear() +
    (targetDate.getTime() - yearStart) / (nextYearStart - yearStart)
  // The live risk year follows the cursor in either zone, story year in the
  // transient frames between hover states (tab 69's logic).
  const riskYear = point?.year ?? swap?.hoveredRiskAreaYear ?? storyYear
  const risk = likelihoodAtYear(TAKEOVER_RISK_LIKELIHOOD_DATA, riskYear)
  const columnName = (active: boolean, name: string) => (
    <div
      className="font-mono font-bold uppercase tracking-wide"
      style={{
        marginTop: style.nameMarginTopPx,
        fontSize: style.nameFontSizePx,
        color: active ? style.activeNameColor : style.idleNameColor,
        transition: style.nameEmphasisTransitionCss,
      }}
    >
      {name}
    </div>
  )
  const columnDefinition = (text: string) => (
    <div
      className="font-sans"
      style={{
        marginTop: style.definitionMarginTopPx,
        fontSize: style.definitionFontSizePx,
        lineHeight: style.definitionLineHeight,
      }}
    >
      {text}
    </div>
  )
  return (
    <div className="relative">
      {/* The gliding focus bar: rides the top edge of whichever column's
          zone owns the hover, sliding across the gap when the pointer
          crosses the line/field boundary. */}
      <div
        className="absolute left-0 top-0"
        style={{
          width: `calc(50% - ${style.columnGapPx / 2}px)`,
          height: style.focusBarHeightPx,
          backgroundColor: style.focusBarColor,
          transform: lineActive
            ? "translateX(0)"
            : `translateX(calc(100% + ${style.columnGapPx}px))`,
          transition: style.focusBarTransitionCss,
        }}
      />
      <div
        className="flex"
        style={{
          gap: style.columnGapPx,
          paddingTop: style.focusBarHeightPx + style.focusBarGapBelowPx,
        }}
      >
        <div className="min-w-0 flex-1">
          <div
            className="flex items-center font-mono"
            style={{ gap: style.iconGapPx }}
          >
            <CapabilityLineIconSmall />
            <span
              className="font-bold whitespace-nowrap"
              style={{ fontSize: style.valueFontSizePx }}
            >
              {point?.speedupText ?? style.speedupPlaceholder}
            </span>
          </div>
          {columnName(lineActive, CAPABILITY_HOVER_COPY.title)}
          {columnDefinition(CELLS_DEF_COLUMNS_COPY.capability)}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="flex items-center font-mono"
            style={{ gap: style.iconGapPx }}
          >
            <DitherKeySwatch
              color={RISK_KEYS_FOOTER_STYLE.swatchRedColor}
              rowRadii={COMBINED_HIERARCHY_STYLE.footnoteSwatchRowRadiiRed}
            />
            <span
              className="font-bold whitespace-nowrap"
              style={{
                fontSize: style.valueFontSizePx,
                color: COMBINED_RISK_LINE_STYLE.valueColor,
              }}
            >
              {risk !== null ? fmtLikelihood(risk) : ""}
            </span>
          </div>
          {columnName(!lineActive, TAKEOVER_RISK_HOVER_COPY.title)}
          {columnDefinition(CELLS_DEF_COLUMNS_COPY.risk)}
        </div>
      </div>
      {swap?.zoneColumnsLevelList && (
        // Tab 77: 76's agent-level design under the columns — the current
        // level tracks the cursor's year in either zone (the line's own
        // point label on the line; the line's level AT the cursor's year
        // while sweeping the field).
        <CompactLevelList
          currentLabel={
            point?.capabilityThresholdLabel ??
            capabilityLevelLabelAtYear(riskYear, activePlan)
          }
        />
      )}
    </div>
  )
}

// Wraps the HUD's entire lower half: while the capability line is hovered the
// children keep their layout footprint but go visibility-hidden, and the
// ladder takeover paints over them. Hovering the takeover-risk field above
// the line instead paints the TakeoverRiskAreaDetail explainer.
export const CapabilityHoverSwapTarget = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const swap = useCapabilityHoverSwap()
  const sequentialFade = useHoverSwapSequentialFade()
  // Non-stock rule treatments (2026-07-11): the capability takeover gets NO
  // separator rule — the ladder starts flush where the covered HUD content
  // starts. (The globe's Data Centers takeover keeps its hung rule via
  // HudHoverTakeoverDetail.)
  const stockRule = useContext(HudHoverRuleTreatmentContext) === "stock"
  const point = swap?.hoveredPoint ?? null
  // The graph publishes the two hovers mutually exclusively; the point hover
  // wins here too in any transient frame carrying both.
  const riskArea = point === null && Boolean(swap?.hoveredTakeoverRiskArea)
  const active = Boolean(point) || riskArea
  return (
    <div className={`relative ${className ?? ""}`}>
      {/* undefined (not "visible") when idle so outer swap targets can still
          hide this block — explicit "visible" would override inherited hidden.
          Opacity carries the crossfade; visibility flips after it so the lower
          half drops out of hit-testing like before. */}
      <div style={hoverSwapHiddenAndUntargetableContentStyle(active, sequentialFade)}>
        {children}
      </div>
      <HoverSwapFadeOverlay
        active={active}
        // The zone-clarity fade (tabs 66–68, 70): keying the content by the
        // hovered ZONE makes a boundary crossing run the same staggered
        // handoff as any item→item hover switch — the old explainer fades
        // out, the new one enters on the stagger — instead of the two
        // panels hard-swapping in one frame. The stacked panel (tab 69)
        // keeps ONE key: its content is the same in both zones and only the
        // red emphasis glides.
        contentKey={
          swap?.zonePanelFade && !swap?.zoneStackedEmphasis && !swap?.zoneColumnsEmphasis
            ? point
              ? "zone-capability"
              : "zone-risk"
            : undefined
        }
        className="absolute top-0 left-0 right-0 z-10"
      >
        {(() => {
          const detail = swap?.zoneStackedEmphasis && active ? (
            <ZoneEmphasisStackedDetail point={point} />
          ) : swap?.zoneColumnsEmphasis && active ? (
            <ZoneEmphasisColumnsDetail point={point} />
          ) : point ? (
            <>
              {swap?.combinedRiskDetail ? (
                // The combined hover (tab 14): capability + foom-risk in one
                // explainer — see CombinedCapabilityRiskDetail.
                <CombinedCapabilityRiskDetail
                  point={point}
                  ladderRowHighlightDisabled={swap?.ladderRowHighlightDisabled}
                  ladderHighlight={swap?.ladderHighlight}
                  ladderDescriptionLayout={swap?.ladderDescriptionLayout}
                />
              ) : (
                <CapabilityLadder
                  point={point}
                  ladderRowHighlightDisabled={swap?.ladderRowHighlightDisabled}
                  ladderHighlight={swap?.ladderHighlight}
                  ladderDescriptionLayout={swap?.ladderDescriptionLayout}
                />
              )}
              {/* Clicking the line opens the capability details panel, so
                  the takeover advertises it like the below-HUD details. */}
              <HudHoverClickHint />
            </>
          ) : riskArea ? (
            <TakeoverRiskAreaDetail />
          ) : null
          return stockRule ? (
            <HudHoverTakeoverDetail>{detail}</HudHoverTakeoverDetail>
          ) : (
            <div
              style={{
                paddingTop: swap?.takeoverDetailFlushTop
                  ? 0
                  : TAKEOVER_DETAIL_TOP_NUDGE_PX,
              }}
            >
              {detail}
            </div>
          )
        })()}
      </HoverSwapFadeOverlay>
    </div>
  )
}

// Tab 74 (capabilityHoverRowOnly): while the graph hover replaces the
// policies/power row, the labor strip below stays LIVE — this rule hangs in
// the gap between the swapped row and the strip, marking where the takeover
// ends. Same component and per-tab treatment as every other hover separator
// (HudHoverDetailRule), hung at the shared centered offset so it sits
// exactly where the labor hover's own rule-above appears. Render inside a
// relative wrapper around the labor strip. Fades on the HUD's default hover
// clocks: 120ms strong ease-out in, 45ms linear cut out.
export const CapabilityHoverLaborDivider = () => {
  const swap = useCapabilityHoverSwap()
  const active =
    Boolean(swap?.hoveredPoint) || Boolean(swap?.hoveredTakeoverRiskArea)
  return (
    <HudHoverDetailRule
      className="absolute inset-x-0"
      style={{
        top: HUD_HOVER_RULE_CENTERED_TOP_PX,
        opacity: active ? 1 : 0,
        transition: active
          ? "opacity 120ms cubic-bezier(0.16, 1, 0.3, 1)"
          : "opacity 45ms linear",
      }}
    />
  )
}
